import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_CONFIG } from '../../../../utils/apiConfig';
import FirebaseService from '../../../../services/firebaseService.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DOMPurify from 'dompurify';
import { applyPlugin } from 'jspdf-autotable';
import { QRCodeCanvas } from 'qrcode.react';
import QRCode from 'qrcode';
import ProductSelector from '../../../Product/ProductSelector.js';
import watermark from '../../../../assets/images/watermark.png';
import cancelledWatermark from '../../../../assets/images/cancelled.png';
import defaultLogo from '../../../../assets/images/Wyenfos_bills_logo.png';
import wyenfosLogo from '../../../../assets/company-logos/wyenfos.png';
import wyenfosAdsLogo from '../../../../assets/company-logos/wyenfos_ads.png';
import wyenfosCashLogo from '../../../../assets/company-logos/wyenfos_cash.png';
import { showNotification } from '../utils/showNotification.js';
import { getCompanyDetails, getLogoUrl } from '../utils/companyHelpers.js';
import './CashBill.css';

applyPlugin(jsPDF);

const userEmail = localStorage.getItem('userEmail') || 'unknown@example.com';
const userRole = localStorage.getItem('userRole') || 'staff';
const validCompanies = [
  'WYENFOS INFOTECH',
  'WYENFOS GOLD & DIAMONDS',
  'WYENFOS GOLD AND DIAMONDS',
  'WYENFOS ADS',
  'WYENFOS CASH VAPASE',
  'AYUR4LIFE HERBALS INDIA',
  'WYENFOS',
  'WYENFOS PURE DROPS',
  'WYENFOS CHARITABLE TRUST',
];

const companyPrefixes = {
  'WYENFOS INFOTECH': 'WIT',
  'WYENFOS GOLD AND DIAMONDS': 'WGD',
  'WYENFOS ADS': 'WAD',
  'WYENFOS CASH VAPASE': 'WCV',
  'AYUR4LIFE HERBALS INDIA': 'ALH',
  'WYENFOS': 'WNF',
  'WYENFOS PURE DROPS': 'WPD',
};

// Dual-logo companies configuration
const dualLogoCompanies = {
  'WYENFOS ADS': {
    mainLogo: wyenfosLogo,        // WYENFOS main logo (not infotech)
    centerLogo: wyenfosAdsLogo,   // WYENFOS ADS specific logo
    centerLogoAlt: 'WYENFOS ADS Logo'
  },
  'WYENFOS CASH VAPASE': {
    mainLogo: wyenfosLogo,        // WYENFOS main logo (not infotech)
    centerLogo: wyenfosCashLogo,  // CASH VAPASE specific logo
    centerLogoAlt: 'WYENFOS CASH VAPASE Logo'
  }
};

// Function to get logo configuration for dual-logo companies
const getLogoConfiguration = (companyName) => {
  if (dualLogoCompanies[companyName]) {
    return {
      isDualLogo: true,
      mainLogo: dualLogoCompanies[companyName].mainLogo,
      centerLogo: dualLogoCompanies[companyName].centerLogo,
      centerLogoAlt: dualLogoCompanies[companyName].centerLogoAlt
    };
  }
  
  return {
    isDualLogo: false,
    mainLogo: getLogoUrl(getCompanyDetails(companyName).logo, companyName),
    centerLogo: null,
    centerLogoAlt: null
  };
};





class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
  }

  render() {
    if (this.state.hasError) {
      return (
        <div>
          <h1>Something went wrong.</h1>
          <p>{this.state.error?.message || 'Unknown error'}</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Create API instance only if BASE_URL is available
const api = API_CONFIG.BASE_URL ? axios.create({
  baseURL: `${API_CONFIG.BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
}) : null;

// Add interceptors only if API is available
if (api) {
api.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('token');
    
    // If no token, try to get from Firebase Auth
    if (!token) {
      try {
        const { auth } = await import('../../../../firebase');
        const user = auth.currentUser;
        if (user) {
          token = await user.getIdToken(true);
          localStorage.setItem('token', token);
        }
      } catch (error) {
      }
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      localStorage.removeItem('token');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);
}

const debounce = (func, delay = 500) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

const sanitizeInput = (input) => DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

function CashBill() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Handle company selection from URL parameters
  let selectedCompanyFromState = 'WYENFOS';
  
  // First try to get company from URL parameters
  const companyFromUrl = searchParams.get('company');
  if (companyFromUrl) {
    selectedCompanyFromState = decodeURIComponent(companyFromUrl);
  } else {
    // Fallback to location state for backward compatibility
    const selectedCompany = location.state?.selectedCompany;
    if (selectedCompany) {
      // If selectedCompany is an object, extract the name
      if (typeof selectedCompany === 'object' && selectedCompany.name) {
        selectedCompanyFromState = selectedCompany.name;
      } else if (typeof selectedCompany === 'string') {
        selectedCompanyFromState = selectedCompany;
      }
    }
  }
  
  // Handle company name mapping for backward compatibility
  if (selectedCompanyFromState === 'AYUR 4 LIFE HERBALS India') {
    selectedCompanyFromState = 'AYUR4LIFE HERBALS INDIA';
  }
  
  // Handle company name mapping for WYENFOS GOLD
  if (selectedCompanyFromState === 'WYENFOS GOLD & DIAMONDS') {
    selectedCompanyFromState = 'WYENFOS GOLD AND DIAMONDS';
  }
  
  // Ensure selectedCompanyFromState is always a string
  if (typeof selectedCompanyFromState !== 'string') {
    selectedCompanyFromState = 'WYENFOS';
  }



  const companyDetails = getCompanyDetails(selectedCompanyFromState);

  const [company, setCompany] = useState(companyDetails);
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    accountNumber: '',
    ifsc: '',
    swiftCode: '',
    branch: '',
    upiId: '',
    qrCodeUrl: ''
  });
  const [qrCodeData, setQrCodeData] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [savedBills, setSavedBills] = useState([]);
  const [editPermissions, setEditPermissions] = useState({});
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [pendingEditBill, setPendingEditBill] = useState(null);
  // Permission status is now handled via email notifications
  // ProductList is now handled by ProductSelector component
  const [products, setProducts] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentBillId, setCurrentBillId] = useState(null);
  const [isCreatingCustomer] = useState(false);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [manualEntryIndex, setManualEntryIndex] = useState(null);
  const [manualEntryData, setManualEntryData] = useState({
    itemname: '',
    code: '',
    hsnSac: '',
    quantity: 1,
    rate: '',
    taxRate: 18
  });
  const debitRef = useRef({});
  const [form, setForm] = useState({
    invoiceNumber: '',
    customerId: '',
    customerName: '',
    customerContact: { address: '', phone: '', email: '', gstin: '' },
    items: [{ code: '', itemname: '', hsnSac: '', quantity: 1, unit: 'Nos', rate: '', taxRate: '' }],
    isOtherState: false,
    paymentTerms: 'Cash',
    remarks: '',
    isCancelled: false,
    manualRoundOff: '',
    date: new Date().toISOString().slice(0, 10),
    company,
  });
  const [errors, setErrors] = useState({
    customerId: '',
    customerName: '',
    email: '',
    phone: '',
    gstin: '',
    items: [{ code: '', itemname: '', hsnSac: '', rate: '', taxRate: '' }],
  });

  const fetchBankDetails = useCallback(async () => {
    if (!company?.name) return;
    
    
    try {
      const firebaseBankDetails = await FirebaseService.getBankDetails(company.name);
      
      if (firebaseBankDetails) {
        
        const bankData = {
          bankName: firebaseBankDetails.bankName || '',
          accountNumber: firebaseBankDetails.accountNumber || '',
          ifsc: firebaseBankDetails.ifscCode || firebaseBankDetails.ifsc || '',
          swiftCode: firebaseBankDetails.swiftCode || '',
          branch: firebaseBankDetails.branch || '',
          upiId: firebaseBankDetails.upiId || '',
          qrCodeUrl: firebaseBankDetails.qrCodeUrl || ''
        };
        
        setBankDetails(bankData);
        
        if (bankData.upiId) {
          const qrData = `upi://pay?pa=${bankData.upiId}&pn=${company.name}&cu=INR`;
          setQrCodeData(qrData);
        } else {
          setQrCodeData('');
        }
        
        return;
      }
      
      
      const emptyBankData = {
        bankName: '',
        accountNumber: '',
        ifsc: '',
        swiftCode: '',
        branch: '',
        upiId: '',
        qrCodeUrl: ''
      };
      
      setBankDetails(emptyBankData);
      setQrCodeData('');
      
      setMessage({ 
        type: 'error', 
        text: `❌ CRITICAL: No bank details found for ${company.name}. Please add bank details to Firebase before processing transactions!` 
      });
      setTimeout(() => setMessage(null), 10000);
      
    } catch (error) {
      
      // Use the same empty bank details structure
      setBankDetails({
        bankName: '',
        accountNumber: '',
        ifsc: '',
        swiftCode: '',
        branch: '',
        upiId: '',
        qrCodeUrl: ''
      });
      setQrCodeData('');
      
      setMessage({ 
        type: 'error', 
        text: `❌ CRITICAL ERROR: Cannot fetch bank details for ${company.name}. Check Firebase connection and ensure bank details exist!` 
      });
      setTimeout(() => setMessage(null), 10000);
      return;
    }
    
    let companyNameForAPI = company.name;
    if (company.name === 'AYUR 4 LIFE HERBALS India') {
      companyNameForAPI = 'AYUR4LIFE HERBALS INDIA';
    }
    
    
    try {
      if (api && !API_CONFIG.USE_FIREBASE_ONLY) {
      const response = await api.get('/bank-details', {
        params: { companyName: companyNameForAPI },
      });
      const data = response.data;
      
      if (data) {
        const bankData = {
          bankName: data.bankName || '',
          accountNumber: data.accountNumber || '',
          ifsc: data.ifsc || '',
          swiftCode: data.swiftCode || '',
          branch: data.branch || '',
          upiId: data.upiId || '',
          qrCodeUrl: data.qrCodeUrl || ''
        };
        
        setBankDetails(bankData);
        
        if (data.upiId) {
          setQrCodeData(data.upiId);
        } else if (data.accountNumber && data.ifsc) {
          const qrData = `upi://pay?pa=${data.accountNumber}@${data.ifsc}&pn=${company.name}&am=&tn=Payment`;
          setQrCodeData(qrData);
        } else {
          const fallbackQrData = `upi://pay?pn=${company.name}&am=&tn=Payment`;
          setQrCodeData(fallbackQrData);
        }
      } else {
        }
      } else {
      }
    } catch (error) {
      const companyBankDetails = {
        'WYENFOS GOLD AND DIAMONDS': {
          bankName: 'State Bank of India',
          accountNumber: '12345678901',
          ifsc: 'SBIN0001234',
          swiftCode: 'SBININBB',
          branch: 'Thrissur Main Branch',
          upiId: 'wyenfosgold@sbi',
          qrCodeUrl: ''
        },
        'WYENFOS INFOTECH': {
          bankName: 'HDFC Bank',
          accountNumber: '50100123456789',
          ifsc: 'HDFC0001234',
          swiftCode: 'HDFCINBB',
          branch: 'Thrissur Branch',
          upiId: 'wyenfostech@hdfc',
          qrCodeUrl: ''
        },
        'AYUR4LIFE HERBALS INDIA': {
          bankName: 'Canara Bank',
          accountNumber: '98765432101',
          ifsc: 'CNRB0001234',
          swiftCode: 'CNRBINBB',
          branch: 'Thrissur Branch',
          upiId: 'ayurherbals@canara',
          qrCodeUrl: ''
        }
      };
      
      const fallbackBankData = companyBankDetails[company.name] || {
        bankName: 'State Bank of India',
        accountNumber: '1234567890',
        ifsc: 'SBIN0001234',
        swiftCode: 'SBININBB',
        branch: 'Main Branch',
        upiId: 'company@sbi',
        qrCodeUrl: ''
      };
      setBankDetails(fallbackBankData);
      
      const fallbackQrData = `upi://pay?pa=${fallbackBankData.upiId}&pn=${company.name}&cu=INR`;
      setQrCodeData(fallbackQrData);
    }
  }, [company]);

useEffect(() => {
  fetchBankDetails();
}, [fetchBankDetails]);

  const fetchLatestBillNumber = useCallback(async () => {
    if (!company?.name) {
      return;
    }
    
    const prefix = companyPrefixes[company.name] || company.prefix || company.name.slice(0, 4).toUpperCase();
    
    if (!prefix) {
      setMessage({ type: 'error', text: 'Company prefix not found.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    setLoading(true);
    try {
      
      let existingBills = [];
      try {
        existingBills = await FirebaseService.getCashBills(company.name, 100); // Get more bills to find the latest
      } catch (firebaseError) {
      }
      
      const localBills = JSON.parse(localStorage.getItem('cashbills') || '[]');
      const localCompanyBills = localBills.filter(bill => 
        bill.company?.name === company.name && 
        bill.invoiceNumber?.startsWith(prefix)
      );
      
      // Combine all cashbills for this company
      const allCompanyBills = [...existingBills, ...localCompanyBills];
      
      // Find the highest invoice number for this company and prefix
      let highestNumber = 0;
      allCompanyBills.forEach(bill => {
        if (bill.invoiceNumber && bill.invoiceNumber.startsWith(prefix)) {
          const match = bill.invoiceNumber.match(/(\d+)$/);
          if (match) {
            const num = parseInt(match[1]);
            if (num > highestNumber) {
              highestNumber = num;
            }
          }
        }
      });
      
      const nextNumber = highestNumber + 1;
      const invoiceNumber = `${prefix}-${nextNumber}`;
      
      setForm((prev) => ({
        ...prev,
        invoiceNumber,
      }));
      
      if (nextNumber === 1) {
        setMessage({ type: 'info', text: `Starting fresh sequence: ${invoiceNumber}` });
      } else {
        setMessage({ type: 'info', text: `Continuing sequence: ${invoiceNumber}` });
      }
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error) {
      // Final fallback - start from 1
      const invoiceNumber = `${prefix}-1`;
      setForm((prev) => ({
        ...prev,
        invoiceNumber,
      }));
      setMessage({ type: 'warning', text: `Using fallback bill number: ${invoiceNumber}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setLoading(false);
    }
  }, [company]);

  const fetchAllBills = useCallback(async () => {
    setLoading(true);
    try {
      let apiBills = [];
      let firebaseBills = [];
      
      
      // Try to fetch from API first (only if API is available)
      if (api && !API_CONFIG.USE_FIREBASE_ONLY) {
        try {
          const response = await api.get('/cashbills', {
            params: { company: company?.name },
            timeout: 10000 // 10 second timeout
          });
          apiBills = response.data.bills || response.data || [];
        } catch (apiError) {
          // If it's a network error, show user-friendly message
          if (apiError.code === 'ERR_NETWORK' || apiError.message.includes('ERR_INSUFFICIENT_RESOURCES')) {
            setMessage({ 
              type: 'warning', 
              text: '⚠️ Server connection issue. Using offline mode with Firebase/localStorage.' 
            });
            setTimeout(() => setMessage(null), 5000);
          }
        }
      }
      
      // Always try Firebase as well (not just as fallback)
      try {
        // First try with company filter
        firebaseBills = await FirebaseService.getCashBills(company?.name, 20);
        
        // If no bills found with company filter, try without filter to see all bills
        if (firebaseBills.length === 0) {
          firebaseBills = await FirebaseService.getCashBills(null, 50);
        }
      } catch (firebaseError) {
      }
      
      // Get local bills
      const localBills = JSON.parse(localStorage.getItem('cashbills') || '[]');
      
      // Filter local bills by company if specified
      const filteredLocalBills = company?.name 
        ? localBills.filter(bill => bill.company?.name === company.name)
        : localBills;
      
      // Combine all bills
      let allBills = [...apiBills, ...firebaseBills, ...filteredLocalBills];
      
      // If no bills found with company filter, show all bills for debugging
      if (allBills.length === 0 && (apiBills.length > 0 || firebaseBills.length > 0)) {
        allBills = [...apiBills, ...firebaseBills, ...localBills];
      }
      
      // Sort by date
      allBills.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.date || 0);
        const dateB = new Date(b.createdAt || b.date || 0);
        return dateB - dateA;
      });
      
      // Remove duplicates
      const uniqueBills = allBills.filter((bill, index, self) => 
        index === self.findIndex(b => b.invoiceNumber === bill.invoiceNumber)
      );
      
      // Limit to latest 5 bills
      const latestBills = uniqueBills.slice(0, 5);
      setSavedBills(latestBills);
    } catch (error) {
      const localBills = JSON.parse(localStorage.getItem('cashbills') || '[]');
      setSavedBills(localBills.slice(0, 5));
    } finally {
      setLoading(false);
    }
  }, [company]);

  const checkUserRole = useCallback(async () => {
    // Get fresh token if needed
    let token = localStorage.getItem('token');
    if (!token) {
      try {
        const { auth } = await import('../../../../firebase');
        const user = auth.currentUser;
        if (user) {
          token = await user.getIdToken(true);
          localStorage.setItem('token', token);
        }
      } catch (error) {
      }
    }
    
    const fallbackPermissions = userRole === 'admin' || userRole === 'super_admin' || userRole === 'staff';
    setEditPermissions(fallbackPermissions);
  }, []);

  useEffect(() => {
    
    // Clear any existing error messages on component load
    setMessage(null);
    
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      if (tokenData.exp && tokenData.exp < currentTime) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
    } catch (error) {
      localStorage.removeItem('token');
      navigate('/login');
      return;
    }
    
    // Get company from URL parameters or location state
    const companyFromUrl = searchParams.get('company');
    const selectedCompany = location.state?.selectedCompany;
    let companyToSet = null;
    
    if (companyFromUrl) {
      // Company from URL parameter
      const companyName = decodeURIComponent(companyFromUrl);
      companyToSet = getCompanyDetails(companyName);
    } else if (selectedCompany && selectedCompany.name) {
      // Company from location state (backward compatibility)
      companyToSet = selectedCompany;
      if (selectedCompany.name === 'AYUR 4 LIFE HERBALS India') {
        companyToSet = {
          ...selectedCompany,
          name: 'AYUR4LIFE HERBALS INDIA',
          prefix: 'ALH'
        };
      }
    } else {
      setMessage({ type: 'error', text: 'No company selected. Please select a company first.' });
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      return;
    }
    
    if (companyToSet) {
      setCompany(companyToSet);
    }
    
    // Only run role check once on initial load
    checkUserRole();
    
  }, []); // Empty dependency array to run only once

  const getOrGenerateCustomerId = async (name) => {
    if (!name.trim()) {
      setSearchResults([]);
      setMessage({ type: 'warning', text: 'Please enter a customer name to search.' });
      setTimeout(() => setMessage(null), 3000);
      return '';
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setMessage({ type: 'error', text: 'Please log in to search for customers.' });
      setTimeout(() => setMessage(null), 3000);
      setSearchResults([]);
      return '';
    }
    try {
      
      let customers = [];
      const searchTerm = sanitizeInput(name).toLowerCase();
      
      if (api && !API_CONFIG.USE_FIREBASE_ONLY) {
        try {
          const response = await api.get('/api/customers/find', {
        params: { query: sanitizeInput(name) },
      });
          customers = Array.isArray(response.data) ? response.data : [];
        } catch (apiError) {
        }
      }
      
      if (customers.length === 0) {
        try {
          const firebaseCustomers = await FirebaseService.getCustomers(searchTerm);
          if (firebaseCustomers && firebaseCustomers.length > 0) {
            customers = firebaseCustomers.filter(customer => 
              customer.customerName?.toLowerCase().includes(searchTerm) ||
              customer.customerId?.toLowerCase().includes(searchTerm) ||
              customer.customerContact?.phone?.includes(name) ||
              customer.customerContact?.email?.toLowerCase().includes(searchTerm)
            );
          }
        } catch (firebaseError) {
        }
      }
      
      if (customers.length === 0) {
        const localCustomers = JSON.parse(localStorage.getItem('customers') || '[]');
        
        customers = localCustomers.filter(customer => 
          customer.customerName?.toLowerCase().includes(searchTerm) ||
          customer.customerId?.toLowerCase().includes(searchTerm) ||
          customer.customerContact?.phone?.includes(name) ||
          customer.customerContact?.email?.toLowerCase().includes(searchTerm)
        );
        
      }
      
      setSearchResults(customers);

      if (customers.length > 0) {
        const customer = customers[0];
        
        setForm((prev) => ({
          ...prev,
          customerId: customer.customerId || '',
          customerName: customer.customerName || '',
          customerContact: {
            address: customer.customerContact?.address || '',
            phone: customer.customerContact?.phone || '',
            email: customer.customerContact?.email || '',
            gstin: customer.customerContact?.gstin || '',
          },
        }));
        setMessage({ type: 'success', text: `Customer found: ${customer.customerName}` });
        setTimeout(() => setMessage(null), 3000);
        return customer.customerId || '';
      }

      setMessage({ type: 'info', text: `Customer "${name}" not found. You can continue by filling customer details manually.` });
      setTimeout(() => setMessage(null), 5000);
      
      setForm((prev) => ({
        ...prev,
        customerId: '',
        customerName: name, // Keep the search term for potential creation
        customerContact: { address: '', phone: '', email: '', gstin: '' }
      }));
      
      return '';
    } catch (err) {
      setMessage({
        type: 'error',
        text: `Failed to search for customer: ${err.response?.data?.message || err.message}`,
      });
      setTimeout(() => setMessage(null), 3000);
      setSearchResults([]);
      return '';
    }
  };

  const debouncedSearch = useRef(debounce(getOrGenerateCustomerId, 500)).current;

  useEffect(() => {
    if (customerSearch.length >= 3) {
      debouncedSearch(customerSearch);
    } else {
      setSearchResults([]);
    }
  }, [customerSearch, debouncedSearch]);


  useEffect(() => {
    if (company && company.name) {
      // Only fetch data when company actually changes
      fetchAllBills();
      fetchLatestBillNumber();
    }
  }, [company, fetchAllBills, fetchLatestBillNumber]);

  useEffect(() => {
  }, [qrCodeData]);

  const selectCustomer = (customer) => {
    
    // Validate customer data
    if (!customer.customerId || !customer.customerName) {
      setMessage({ type: 'error', text: 'Invalid customer data. Please try searching again.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setForm(prev => ({
      ...prev,
      customerId: customer.customerId || '',
      customerName: customer.customerName || '',
      customerContact: {
        address: customer.customerContact?.address || '',
        phone: customer.customerContact?.phone || '',
        email: customer.customerContact?.email || '',
        gstin: customer.customerContact?.gstin || '',
      },
    }));

    setErrors(prev => ({
      ...prev,
      customerId: customer.customerId && customer.customerId.match(/^CUST-\d+$/) ? '' : 'Customer ID must be in format CUST-{number}',
      customerName: '',
      email: '',
      phone: '',
      gstin: '',
      address: '',
    }));

    setCustomerSearch('');
    setSearchResults([]);

    setMessage({ 
      type: 'success', 
      text: `Customer selected: ${customer.customerName} (${customer.customerId})` 
    });
    setTimeout(() => setMessage(null), 3000);

  };


   useEffect(() => {
    const companyFromUrl = searchParams.get('company');
    const selectedCompany = location.state?.selectedCompany;
    
    if (!companyFromUrl && (!selectedCompany || !selectedCompany.name)) {
      navigate('/dashboard');
    }
  }, [searchParams, location.state, navigate]);

 const fetchCustomerByContact = async (field, value) => {
    if (!value) return null;
    
    const searchTerm = sanitizeInput(value).toLowerCase().trim();
    
    let companyNameForAPI = company.name;
    if (company.name === 'AYUR 4 LIFE HERBALS India') {
      companyNameForAPI = 'AYUR4LIFE HERBALS INDIA';
    }
    
    try {
      if (api && !API_CONFIG.USE_FIREBASE_ONLY) {
        const response = await api.get('/api/customers/find', {
        params: { query: value, company: companyNameForAPI },
      });
        
        if (response.data && Array.isArray(response.data)) {
          const customer = response.data.find((c) => {
            const nameMatch = c.customerName?.toLowerCase().includes(searchTerm);
            const idMatch = c.customerId?.toLowerCase().includes(searchTerm);
            const phoneMatch = c.customerContact?.phone?.includes(value);
            const emailMatch = c.customerContact?.email?.toLowerCase().includes(searchTerm);
            
            return nameMatch || idMatch || phoneMatch || emailMatch;
          });
          
          if (customer) {
            return customer;
          }
        }
      }
    } catch (apiError) {
    }
    
    try {
      const firebaseCustomers = await FirebaseService.getCustomers(searchTerm);
      
      if (firebaseCustomers && firebaseCustomers.length > 0) {
        const customer = firebaseCustomers.find((c) => {
          const nameMatch = c.customerName?.toLowerCase().includes(searchTerm);
          const idMatch = c.customerId?.toLowerCase().includes(searchTerm);
          const phoneMatch = c.customerContact?.phone?.includes(value);
          const emailMatch = c.customerContact?.email?.toLowerCase().includes(searchTerm);
          
          return nameMatch || idMatch || phoneMatch || emailMatch;
        });
        
        if (customer) {
          return customer;
        }
      }
    } catch (firebaseError) {
    }
    
    // Final fallback to localStorage search
    const storedCustomers = JSON.parse(localStorage.getItem('customers') || '[]');
    const customer = storedCustomers.find((c) => {
      const nameMatch = c.customerName?.toLowerCase().includes(searchTerm);
      const idMatch = c.customerId?.toLowerCase().includes(searchTerm);
      const phoneMatch = c.customerContact?.phone?.includes(value);
      const emailMatch = c.customerContact?.email?.toLowerCase().includes(searchTerm);
      
      return nameMatch || idMatch || phoneMatch || emailMatch;
    });
    
    if (customer) {
    } else {
    }
    
      return customer || null;
  };

  // Function to update customer details in localStorage and Firebase
  const updateCustomerInStorage = async (customerId, field, value) => {
    try {
      // Update in localStorage
      const existingCustomers = JSON.parse(localStorage.getItem('customers') || '[]');
      const customerIndex = existingCustomers.findIndex(c => c.customerId === customerId);
      
      if (customerIndex !== -1) {
        // Update existing customer in localStorage
        if (field === 'name') {
          existingCustomers[customerIndex].customerName = value;
        } else {
          existingCustomers[customerIndex].customerContact[field] = value;
        }
        existingCustomers[customerIndex].updatedAt = new Date().toISOString();
        existingCustomers[customerIndex].lastUpdatedBy = userEmail;
        
        localStorage.setItem('customers', JSON.stringify(existingCustomers));
        
        // Also try to update in Firebase if available
        try {
          const updatedCustomerData = existingCustomers[customerIndex];
          await FirebaseService.updateCustomer(customerId, updatedCustomerData);
        } catch (firebaseError) {
        }
      }
    } catch (error) {
    }
  };

  // Function to view all stored customers (for debugging)
  const viewStoredCustomers = () => {
    const customers = JSON.parse(localStorage.getItem('customers') || '[]');
    return customers;
  };

  // Add to window for debugging (can be called from console)
  window.viewStoredCustomers = viewStoredCustomers;
  
  // Add debugging function to view bill numbers (for console debugging)
  window.viewBillNumbers = () => {
    const billNumbers = {};
    Object.keys(companyPrefixes).forEach(company => {
      const prefix = companyPrefixes[company];
      const storageKey = `lastBillNumber_${prefix}`;
      billNumbers[company] = {
        prefix,
        lastNumber: localStorage.getItem(storageKey) || '0',
        nextNumber: `${prefix}-${(parseInt(localStorage.getItem(storageKey) || '0') + 1)}`
      };
    });
    console.table(billNumbers);
    return billNumbers;
  };

  // Add debugging function to fetch all bills from Firebase (for console debugging)
  window.debugFetchAllBills = async () => {
    try {
      const allBills = await FirebaseService.getCashBills(null, 50);
      return allBills;
    } catch (error) {
      return [];
    }
  };

  // Add debugging function to test API directly
  window.debugAPICashBills = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/cashbills/debug`);
      const data = await response.json();
      return data;
    } catch (error) {
      return null;
    }
  };

  // Add debugging function to test API with company filter
  window.debugAPICashBillsWithCompany = async (companyName) => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/cashbills?company=${encodeURIComponent(companyName)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      return null;
    }
  };

  // Add function to force show all bills (bypass company filtering)
  window.forceShowAllBills = async () => {
    try {
      const allBills = await FirebaseService.getCashBills(null, 50);
      setSavedBills(allBills.slice(0, 5));
      return allBills;
    } catch (error) {
      return [];
    }
  };

  // Add function to refresh bills after save
  const refreshBillsAfterSave = useCallback(async () => {
    try {
      // Try to fetch bills with current company first
      let bills = await FirebaseService.getCashBills(company?.name, 20);
      
      // If no bills found with company filter, show all bills
      if (bills.length === 0) {
        bills = await FirebaseService.getCashBills(null, 20);
      }
      
      setSavedBills(bills.slice(0, 5));
    } catch (error) {
    }
  }, [company?.name]);

  window.resetBillCounter = (companyName) => {
    const prefix = companyPrefixes[companyName];
    if (prefix) {
      const storageKey = `lastBillNumber_${prefix}`;
      localStorage.setItem(storageKey, '0');
      
      if (selectedCompanyFromState === companyName) {
        setForm(prev => ({
          ...prev,
          invoiceNumber: `${prefix}-1`
        }));
      }
      
      return `${prefix}-1`;
    } else {
      return null;
    }
  };

  window.migrateAFHtoALH = () => {
    const oldKey = 'lastBillNumber_AFH';
    const newKey = 'lastBillNumber_ALH';
    
    const oldValue = localStorage.getItem(oldKey);
    if (oldValue) {
      localStorage.setItem(newKey, oldValue);
      localStorage.removeItem(oldKey);
      
      if (selectedCompanyFromState === 'AYUR4LIFE HERBALS INDIA') {
        const nextNumber = parseInt(oldValue) + 1;
        setForm(prev => ({
          ...prev,
          invoiceNumber: `ALH-${nextNumber}`
        }));
      }
    } else {
    }
  };

  window.resetAllBillCounters = () => {
    
    Object.keys(companyPrefixes).forEach(companyName => {
      const prefix = companyPrefixes[companyName];
      const storageKey = `lastBillNumber_${prefix}`;
      localStorage.setItem(storageKey, '0');
    });
    
    const currentCompany = selectedCompanyFromState;
    const currentPrefix = companyPrefixes[currentCompany];
    if (currentPrefix) {
      setForm(prev => ({
        ...prev,
        invoiceNumber: `${currentPrefix}-1`
      }));
    }
    
    return 'All counters reset to start from prefix-1';
  };

  window.ensureSequentialNumbering = (companyName) => {
    const prefix = companyPrefixes[companyName];
    if (!prefix) {
      return null;
    }
    
    const storageKey = `lastBillNumber_${prefix}`;
    localStorage.setItem(storageKey, '0');
    
    
    if (selectedCompanyFromState === companyName) {
      setForm(prev => ({
        ...prev,
        invoiceNumber: `${prefix}-1`
      }));
    }
    
    return `${prefix}-1`;
  };

  const updateAllCustomerDetails = async (customerId) => {
    if (!customerId || !form.customerContact) return;
    
    try {
      
      const fields = ['address', 'phone', 'email', 'gstin'];
      for (const field of fields) {
        if (form.customerContact[field]) {
          await updateCustomerInStorage(customerId, field, form.customerContact[field]);
        }
      }
      
      setMessage({ 
        type: 'success', 
        text: `Customer details updated successfully for ${form.customerName}` 
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Failed to update customer details' 
      });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const saveCustomerDetails = async () => {
    if (!form.customerName.trim()) {
      setMessage({ type: 'error', text: 'Customer name is required to save customer details.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    const hasDetails = form.customerContact?.address?.trim() || form.customerContact?.phone?.trim() || 
                      form.customerContact?.email?.trim() || form.customerContact?.gstin?.trim();
    
    if (!hasDetails) {
      setMessage({ type: 'warning', text: 'Please fill in at least one detail (address, phone, email, or GSTIN) to save customer.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    const shouldSave = window.confirm(
      `Do you want to save customer "${form.customerName}" with the current details?\n\n` +
      `Address: ${form.customerContact?.address || 'Not provided'}\n` +
      `Phone: ${form.customerContact?.phone || 'Not provided'}\n` +
      `Email: ${form.customerContact?.email || 'Not provided'}\n` +
      `GSTIN: ${form.customerContact?.gstin || 'Not provided'}`
    );

    if (!shouldSave) return;

    setLoading(true);
    try {
      const customerData = {
        customerName: sanitizeInput(form.customerName),
        customerContact: {
          address: form.customerContact?.address || '',
          phone: form.customerContact?.phone || '',
          email: form.customerContact?.email || '',
          gstin: form.customerContact?.gstin || '',
        },
        company: selectedCompanyFromState,
        createdBy: userEmail,
        lastUpdatedBy: userEmail,
      };


      let savedCustomer;
      
      if (API_CONFIG.USE_FIREBASE_ONLY || !API_CONFIG.BASE_URL) {
        savedCustomer = await FirebaseService.addCustomer(customerData);
      } else {
        const response = await api.post('/customers', customerData);
        savedCustomer = response.data;
      }

      if (savedCustomer.customerId && !form.customerId) {
        setForm(prev => ({
          ...prev,
          customerId: savedCustomer.customerId
        }));
      }

      setMessage({ 
        type: 'success', 
        text: `Customer "${savedCustomer.customerName}" saved successfully! ${savedCustomer.customerId ? `(ID: ${savedCustomer.customerId})` : ''}` 
      });
      setTimeout(() => setMessage(null), 4000);

    } catch (err) {
      
      let errorMessage = 'Failed to save customer';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setMessage({ type: 'error', text: `Failed to save customer: ${errorMessage}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const updateCustomerId = (value) => {
    setForm((prev) => ({ ...prev, customerId: value || '' }));
    setErrors((prev) => ({
      ...prev,
      customerId: value && typeof value === 'string' && value.match(/^CUST-\d+$/) ? '' : 'Customer ID must be in format CUST-{number}',
    }));
  };

  const updateCustomerName = async (value) => {
    setForm((prev) => ({ ...prev, customerName: value }));
    setErrors((prev) => ({
      ...prev,
      customerName: value.trim() ? '' : 'Customer name is required',
    }));

    if (form.customerId) {
      updateCustomerInStorage(form.customerId, 'name', value);
    }
    
    if (!currentBillId && value && !form.customerId && !isCreatingCustomer) {
      const customer = await fetchCustomerByContact('name', value);
      if (customer) {
        setForm((prev) => ({
          ...prev,
          customerId: customer.customerId || '',
          customerName: customer.customerName || value,
          customerContact: {
            address: customer.customerContact?.address || '',
            phone: customer.customerContact?.phone || '',
            email: customer.customerContact?.email || '',
            gstin: customer.customerContact?.gstin || '',
          },
        }));
        setErrors((prev) => ({
          ...prev,
          customerId: customer.customerId && typeof customer.customerId === 'string' && customer.customerId.match(/^CUST-\d+$/) ? '' : 'Customer ID must be in format CUST-{number}',
        }));
      }
    }
  };

  const updateCustomerContact = (field, value) => {
    // Sanitize inpt
    const sanitizedValue = sanitizeInput(value);
    
    setForm((prev) => ({
      ...prev,
      customerContact: { ...prev.customerContact, [field]: sanitizedValue },
    }));

    let errorMessage = '';
    
    if (field === 'email' && sanitizedValue) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(sanitizedValue)) {
        errorMessage = 'Please enter a valid email address (e.g., user@example.com)';
      }
    } else if (field === 'phone' && sanitizedValue) {
      // Allow Indian phone numbers (10 digits) with optional +91 or country code
      const phoneRegex = /^(\+91[\s-]?)?[6-9]\d{9}$/;
      const cleanPhone = sanitizedValue.replace(/[\s-]/g, '');
      if (!phoneRegex.test(cleanPhone) && !/^\d{10}$/.test(cleanPhone)) {
        errorMessage = 'Please enter a valid Indian phone number (10 digits starting with 6-9)';
      }
    } else if (field === 'gstin' && sanitizedValue) {
      // GSTIN format: 2 digits (state) + 10 alphanumeric + 1 digit + 1 letter + 1 digit
      const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
      if (!gstinRegex.test(sanitizedValue.toUpperCase())) {
        errorMessage = 'Please enter a valid 15-character GSTIN (e.g., 22AAAAA0000A1Z5)';
      }
    } else if (field === 'address' && sanitizedValue && sanitizedValue.length < 10) {
      errorMessage = 'Address should be at least 10 characters long';
    }

    setErrors((prev) => ({
      ...prev,
      [field]: errorMessage,
    }));

    // Update customer in localStorage and Firebase if customer exists
    if (form.customerId) {
      updateCustomerInStorage(form.customerId, field, sanitizedValue);
    }

    // Auto-search for existing customers when email or phone is entered
    if ((field === 'email' || field === 'phone') && sanitizedValue && !currentBillId && !errorMessage) {
      if (!debitRef.current[field]) {
        debitRef.current[field] = debounce(async (field, value) => {
          const customer = await fetchCustomerByContact(field, value);
          if (customer) {
            setForm((prev) => ({
              ...prev,
              customerId: customer.customerId || '',
              customerName: customer.customerName || prev.customerName,
              customerContact: {
                address: customer.customerContact?.address || prev.customerContact.address,
                phone: customer.customerContact?.phone || prev.customerContact.phone,
                email: customer.customerContact?.email || prev.customerContact.email,
                gstin: customer.customerContact?.gstin || prev.customerContact.gstin,
              }
            }));
            setErrors((prev) => ({
              ...prev,
              customerId: customer.customerId && typeof customer.customerId === 'string' && customer.customerId.match(/^CUST-\d+$/) ? '' : 'Customer ID must be in format CUST-{number}',
            }));
            setMessage({ 
              type: 'info', 
              text: `Found existing customer: ${customer.customerName} (${customer.customerId})` 
            });
            setTimeout(() => setMessage(null), 3000);
          }
        }, 800);
      }
      debitRef.current[field](field, sanitizedValue);
    }
  };

  const updateItem = (index, field, value) => {
    
    // Add safety check for index and items array
    if (index < 0 || index >= form.items.length) {
      return;
    }
    
    setForm((prev) => {
      const newItems = [...prev.items];

      if (field === 'itemname') {
          newItems[index] = { ...newItems[index], itemname: value };
      } else {
        newItems[index] = {
          ...newItems[index],
          [field]:
            field === 'quantity'
              ? Math.max(1, parseInt(value) || 1)
              : field === 'rate'
              ? Math.max(0, parseFloat(value) || 0)
              : field === 'taxRate'
              ? value === ''
                ? ''
                : Math.max(0, parseFloat(value) || 0)
              : value,
        };
      }
      return { ...prev, items: newItems };
    });
    setErrors((prev) => {
      const newItemErrors = [...prev.items];
      newItemErrors[index] = {
        ...newItemErrors[index],
        [field]:
          field === 'itemname' && !value.trim()
            ? 'Item name is required'
            : field === 'hsnSac' && value && !/^\d{4,8}$/.test(value)
            ? 'HSN/SAC must be 4-8 digits'
            : field === 'rate' && (parseFloat(value) <= 0 || isNaN(parseFloat(value)))
            ? 'Rate must be positive'
            : field === 'taxRate' && (value === '' || isNaN(parseFloat(value)) || parseFloat(value) < 0)
            ? 'Tax rate must be a valid non-negative number'
            : field === 'code' && value && !/^[A-Za-z0-9-_ ]+$/.test(value)
            ? 'Item code must be alphanumeric with spaces/dashes'
            : '',
      };
      return { ...prev, items: newItemErrors };
    });
  };

  const handleProductSelect = (index, product) => {
    // Add null check to prevent the error
    if (!product) {
      return;
    }
    
    // Clear validation errors for this item when selecting a product
    setErrors(prev => ({
      ...prev,
      items: prev.items.map((itemErrors, idx) => 
        idx === index 
          ? { ...itemErrors, itemname: '', code: '', hsnSac: '', rate: '', quantity: '', taxRate: '' }
          : itemErrors
      )
    }));
    
    setForm((prev) => {
      const newItems = [...prev.items];
      const updatedItem = {
        ...newItems[index],
        itemname: product.itemName || '',
        code: product.itemCode || '',
        hsnSac: product.hsn || '',
        rate: product.unitPrice || 0,
        taxRate: product.gst || 18,
      };
      newItems[index] = updatedItem;
      
      return { ...prev, items: newItems };
    });
  };

  const handleProductInput = (value, index) => {
    // Always update the itemname field first to allow manual typing
    updateItem(index, 'itemname', value);
    
    // Then check if it matches a product for auto-fill
    const selectedProduct = products.find(p => p.itemName === value.trim());
    if (selectedProduct) {
      // Only auto-fill other fields if the value exactly matches a product name
      handleProductSelect(index, selectedProduct);
    }
  };

  const openManualEntryModal = (index) => {
    const currentItem = form.items[index];
    
    // Add safety check for currentItem
    if (!currentItem) {
      return;
    }
    
    setManualEntryIndex(index);
    setManualEntryData({
      itemname: currentItem.itemname || '',
      code: currentItem.code || '',
      hsnSac: currentItem.hsnSac || '',
      quantity: currentItem.quantity || 1,
      rate: currentItem.rate || '',
      taxRate: currentItem.taxRate || 18
    });
    setShowManualEntryModal(true);
  };

  const closeManualEntryModal = () => {
    setShowManualEntryModal(false);
    setManualEntryIndex(null);
    setManualEntryData({
      itemname: '',
      code: '',
      hsnSac: '',
      quantity: 1,
      rate: '',
      taxRate: 18
    });
  };

  const saveManualEntry = () => {
    if (!manualEntryData.itemname.trim()) {
      setMessage({ type: 'error', text: 'Product name is required.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (manualEntryIndex !== null) {
      setForm((prev) => {
        const newItems = [...prev.items];
        newItems[manualEntryIndex] = {
          ...newItems[manualEntryIndex],
          itemname: manualEntryData.itemname,
          code: manualEntryData.code,
          hsnSac: manualEntryData.hsnSac,
          quantity: Number(manualEntryData.quantity) || 1,
          rate: Number(manualEntryData.rate) || 0,
          taxRate: Number(manualEntryData.taxRate) || 18,
        };
        return { ...prev, items: newItems };
      });
      
      setMessage({ type: 'success', text: 'Product details updated manually.' });
      setTimeout(() => setMessage(null), 3000);
    }
    
    closeManualEntryModal();
  };

  const addNewRow = () => {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, { code: '', itemname: '', hsnSac: '', quantity: 1, unit: 'Nos', rate: 10, taxRate: 18 }],
    }));
    setErrors((prev) => ({
      ...prev,
      items: [...prev.items, { code: '', itemname: '', hsnSac: '', rate: '', taxRate: '' }],
    }));
  };

  const deleteRow = (index) => {
    if (form.items.length === 1) {
      alert('At least one item is required.');
      return;
    }
    if (window.confirm('Are you sure you want to delete this item?')) {
      setForm((prev) => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      }));
      setErrors((prev) => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      }));
    }
  };

  const calculateTotal = () => {
    const items = Array.isArray(form.items) ? form.items : [];
    const taxableAmount = items.reduce((sum, item) => sum + (item.quantity * item.rate || 0), 0);
    let totalTax = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalIGST = 0;

    items.forEach((item) => {
      const itemTotal = item.quantity * item.rate;
      const taxRate = item.taxRate !== '' ? parseFloat(item.taxRate) / 100 : 0.18;
      const itemTax = itemTotal * taxRate;
      totalTax += itemTax;
      if (!form.isOtherState) {
        totalCGST += itemTax / 2;
        totalSGST += itemTax / 2;
      } else {
        totalIGST += itemTax;
      }
    });

    const grandTotal = taxableAmount + totalTax;
    
    // Use manual round off if provided, otherwise calculate automatically
    let roundOff, rounded;
    if (form.manualRoundOff !== '' && !isNaN(parseFloat(form.manualRoundOff))) {
      roundOff = parseFloat(form.manualRoundOff);
      rounded = grandTotal - roundOff; // Subtract round off from grand total
    } else {
      rounded = Math.round(grandTotal);
      roundOff = grandTotal - rounded; // Automatic round off (amount to subtract)
    }
    
    return {
      taxableAmount,
      igstTotal: form.isOtherState ? totalIGST : 0,
      cgstTotal: !form.isOtherState ? totalCGST : 0,
      sgstTotal: !form.isOtherState ? totalSGST : 0,
      grandTotal,
      roundOff,
      rounded,
    };
  };

  const saveBill = async () => {
    const token = localStorage.getItem('token');
    if (!token || !userEmail) {
      setMessage({ type: 'error', text: 'Authentication missing. Please log in again.' });
      navigate('/login');
      return false;
    }

    if (!validCompanies.includes(company.name)) {
      setMessage({ type: 'error', text: `Invalid company selected: ${company.name}` });
      setTimeout(() => setMessage(null), 3000);
      return false;
    }

    if (!form.customerName.trim()) {
      setMessage({ type: 'error', text: 'Customer name is required.' });
      setTimeout(() => setMessage(null), 3000);
      return false;
    }

    let customerId = form.customerId;
    if (!customerId || customerId === 'undefined') {
      try {
        customerId = await createNewCustomer(form.customerName);
        if (!customerId) {
          throw new Error('Failed to create customer');
        }
        setForm((prev) => ({ ...prev, customerId }));
        // Wait for state to update
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        setMessage({ type: 'error', text: `Failed to create customer: ${error.message}` });
        setTimeout(() => setMessage(null), 3000);
        return false;
      }
    } else {
      if (!customerId.match(/^CUST-\d+$/)) {
        setMessage({ type: 'error', text: 'Invalid customer ID format. Please select or create a customer.' });
        setTimeout(() => setMessage(null), 3000);
        return false;
      }
    }

    if (!form.items.every((item) => item.itemname && Number(item.quantity) > 0 && Number(item.rate) >= 0)) {
      setMessage({ type: 'error', text: 'All items must have a name, valid quantity, and rate.' });
      setTimeout(() => setMessage(null), 5000);
      return false;
    }

    // Final validation to ensure customerId is not undefined
    if (!customerId || customerId === 'undefined') {
      setMessage({ type: 'error', text: 'Failed to generate customer ID. Please try again.' });
      setTimeout(() => setMessage(null), 3000);
      return false;
    }

    setLoading(true);
    try {
      const totals = calculateTotal();
      const billData = {
        ...form,
        customerId: customerId, // Explicitly set to ensure it's not undefined
        items: form.items.map((item) => ({
          code: item.code || '',
          itemname: item.itemname,
          hsnSac: item.hsnSac || '',
          quantity: Number(item.quantity) || 1,
          rate: Number(item.rate) || 0,
          taxableValue: Number((Number(item.quantity) || 1) * (Number(item.rate) || 0)),
          taxRate: item.taxRate !== '' ? Number(item.taxRate) : 18,
          cgstRate: !form.isOtherState ? Number(item.taxRate) / 2 : 0,
          cgstAmount: !form.isOtherState
            ? Number(((Number(item.quantity) || 1) * (Number(item.rate) || 0) * (Number(item.taxRate) / 100)) / 2)
            : 0,
          sgstRate: !form.isOtherState ? Number(item.taxRate) / 2 : 0,
          sgstAmount: !form.isOtherState
            ? Number(((Number(item.quantity) || 1) * (Number(item.rate) || 0) * (Number(item.taxRate) / 100)) / 2)
            : 0,
          igstRate: form.isOtherState ? Number(item.taxRate) : 0,
          igstAmount: form.isOtherState
            ? Number((Number(item.quantity) || 1) * (Number(item.rate) || 0) * (Number(item.taxRate) / 100))
            : 0,
        })),
        totals: {
          taxableAmount: Number(totals.taxableAmount),
          cgstTotal: Number(totals.cgstTotal),
          sgstTotal: Number(totals.sgstTotal),
          igstTotal: Number(totals.igstTotal),
          grandTotal: Number(totals.grandTotal),
          roundOff: Number(totals.roundOff),
        },
        company,
        paymentDetails: {
          mode: form.paymentTerms,
          status: 'Paid',
        },
        createdBy: userEmail,
        lastUpdatedBy: userEmail,
      };
      billData.invoiceNumber = form.invoiceNumber;
      let response;
      try {
        // API should always be available now
        if (!api) {
          throw new Error('API not configured properly');
        }
        
        response = await api.post('/cashbills', billData);
      if (!response.data) throw new Error('No data returned from server');
        
        // API save successful
      setCurrentBillId(response.data._id);
        
        // Increment bill number for next bill
        const prefix = companyPrefixes[selectedCompanyFromState] || companyPrefixes[company?.name] || 'WNF';
        const storageKey = `lastBillNumber_${prefix}`;
        const match = form.invoiceNumber.match(/(\d+)$/);
        if (match) {
          const currentNum = parseInt(match[1]);
          localStorage.setItem(storageKey, currentNum.toString());
        }
        
      setMessage({ type: 'success', text: `Bill saved successfully! Invoice #${response.data.invoiceNumber}` });
      showNotification('Cash Bill Created', `Cash bill ${form.invoiceNumber} has been created.`);
      
      // Reset form first, then fetch bills after a short delay
      resetForm();
      
      // Fetch bills after form reset to ensure they're displayed
      setTimeout(() => {
        refreshBillsAfterSave();
      }, 500);
      
      return true;
      } catch (apiError) {
        
        // Try Firebase as fallback
        try {
          const firebaseBill = await FirebaseService.addCashBill(billData);
          
          // Firebase save successful
          setCurrentBillId(firebaseBill._id);
          
          // Increment bill number for next bill
          const prefix = companyPrefixes[selectedCompanyFromState] || companyPrefixes[company?.name] || 'WNF';
          const storageKey = `lastBillNumber_${prefix}`;
          const match = form.invoiceNumber.match(/(\d+)$/);
          if (match) {
            const currentNum = parseInt(match[1]);
            localStorage.setItem(storageKey, currentNum.toString());
          }
          
          setMessage({ type: 'success', text: `Bill saved to Firebase! Invoice #${form.invoiceNumber}` });
          showNotification('Cash Bill Created', `Cash bill ${form.invoiceNumber} has been saved to Firebase.`);
          
          // Reset form first, then fetch bills after a short delay
          resetForm();
          
          // Fetch bills after form reset to ensure they're displayed
          setTimeout(() => {
            refreshBillsAfterSave();
          }, 500);
          
          return true;
        } catch (firebaseError) {
          
          // Final fallback to local storage
          const billId = `bill_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const localBill = {
            ...billData,
            _id: billId,
            id: billId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            source: 'local'
          };
          
          // Save to localStorage
          const existingBills = JSON.parse(localStorage.getItem('cashbills') || '[]');
          existingBills.push(localBill);
          localStorage.setItem('cashbills', JSON.stringify(existingBills));
          
          // Increment bill number for next bill
          const prefix = companyPrefixes[selectedCompanyFromState] || companyPrefixes[company?.name] || 'WNF';
          const storageKey = `lastBillNumber_${prefix}`;
          const match = form.invoiceNumber.match(/(\d+)$/);
          if (match) {
            const currentNum = parseInt(match[1]);
            localStorage.setItem(storageKey, currentNum.toString());
          }
          
          setCurrentBillId(billId);
          setMessage({ type: 'success', text: `Bill saved locally! Invoice #${form.invoiceNumber}` });
          showNotification('Cash Bill Created', `Cash bill ${form.invoiceNumber} has been saved locally.`);
          
          // Reset form first, then fetch bills after a short delay
          resetForm();
          
          // Fetch bills after form reset to ensure they're displayed
          setTimeout(() => {
            refreshBillsAfterSave();
          }, 500);
          
          return true;
        }
      }
    } catch (error) {
      if (error.response?.data?.message?.includes('Invoice number')) {
        // Generate new bill number without incrementing localStorage
        const prefix = companyPrefixes[selectedCompanyFromState] || companyPrefixes[company?.name] || 'WNF';
        const storageKey = `lastBillNumber_${prefix}`;
        const lastNumber = parseInt(localStorage.getItem(storageKey) || '0');
        const nextNumber = lastNumber + 2; // Skip one number to avoid duplicate
        const newInvoiceNumber = `${prefix}-${nextNumber}`;
        
        setForm((prev) => ({
          ...prev,
          invoiceNumber: newInvoiceNumber,
        }));
        
        setMessage({
          type: 'error',
          text: `Duplicate invoice number. New number generated: ${newInvoiceNumber}. Please save again.`,
        });
      } else {
        setMessage({
          type: 'error',
          text: `Failed to save bill: ${error.response?.data?.error || error.message}`,
        });
      }
      return false;
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const createNewCustomer = async (name) => {
    if (!name.trim()) {
      setMessage({ type: 'error', text: 'Customer name is required to create a new customer.' });
      setTimeout(() => setMessage(null), 3000);
      return '';
    }
    const token = localStorage.getItem('token');
    if (!token || !userEmail) {
      setMessage({ type: 'error', text: 'Please log in to create a customer.' });
      setTimeout(() => setMessage(null), 3000);
      return '';
    }

    if (!validCompanies.includes(company.name)) {
      setMessage({ type: 'error', text: `Invalid company: ${company.name}` });
      setTimeout(() => setMessage(null), 3000);
      return '';
    }
    try {
      
      // Always create a new customer with empty contact details
      // The staff can fill in the details after the customer is created
      const payload = {
        customerName: sanitizeInput(name),
        customerContact: {
          address: '',
          phone: '',
          email: '',
          gstin: '',
          associatedWith: null,
        },
        company,
        createdBy: userEmail,
        lastUpdatedBy: userEmail,
      };
      
      // Try API first, then fallback to local generation
      let newCustomer;
      try {
      const response = await api.post('/customers', payload);
        newCustomer = response.data;
      } catch (apiError) {
        
        // Try Firebase as fallback
        try {
          const firebaseCustomer = await FirebaseService.addCustomer({
            customerName: sanitizeInput(name),
            customerContact: {
              address: form.customerContact?.address || '',
              phone: form.customerContact?.phone || '',
              email: form.customerContact?.email || '',
              gstin: form.customerContact?.gstin || '',
              associatedWith: null,
            },
            company: company,
            createdBy: userEmail,
            lastUpdatedBy: userEmail,
          });
          
          newCustomer = firebaseCustomer;
        } catch (firebaseError) {
          
          // Get existing customers from localStorage
          const existingCustomers = JSON.parse(localStorage.getItem('customers') || '[]');
          
          // Generate sequential customer ID
          const lastCustomerId = existingCustomers.length > 0 
            ? Math.max(...existingCustomers.map(c => parseInt(c.customerId.split('-')[1]) || 0))
            : 0;
          const nextId = lastCustomerId + 1;
          
          // Create new customer with complete details
          newCustomer = {
            customerId: `CUST-${nextId}`,
            customerName: sanitizeInput(name),
            customerContact: {
              address: form.customerContact?.address || '',
              phone: form.customerContact?.phone || '',
              email: form.customerContact?.email || '',
              gstin: form.customerContact?.gstin || '',
              associatedWith: null,
            },
            company: company,
            createdBy: userEmail,
            lastUpdatedBy: userEmail,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          // Save to localStorage customers collection
          const updatedCustomers = [...existingCustomers, newCustomer];
          localStorage.setItem('customers', JSON.stringify(updatedCustomers));
        }
        
      }
      
      // Validate that the customer was created with a valid ID
      if (!newCustomer.customerId) {
        throw new Error('Failed to generate customer ID');
      }
      
      
      // Update the form with the new customer information
      const updatedForm = {
        ...form,
        customerId: newCustomer.customerId,
        customerName: newCustomer.customerName || name, // Use the provided name to ensure consistency
        customerContact: {
          address: '',
          phone: '',
          email: '',
          gstin: '',
        },
      };
      setForm(updatedForm);
      setMessage({ type: 'success', text: `Customer created: ${newCustomer.customerName} (${newCustomer.customerId})` });
      setTimeout(() => setMessage(null), 3000);
      
      // Clear search after a small delay to ensure form is updated
      setTimeout(() => {
      setCustomerSearch('');
        setSearchResults([]);
      }, 100);
      
      return newCustomer.customerId || '';
    } catch (err) {
      setMessage({
        type: 'error',
        text: `Failed to create customer: ${err.response?.data?.message || err.message}`,
      });
      setTimeout(() => setMessage(null), 3000);
      return '';
    }
  };

  const validateForm = () => {
    if (currentBillId && !form.customerId) {
      setMessage({ type: 'error', text: 'Customer ID is required for existing bills.' });
      setTimeout(() => setMessage(null), 5000);
      return false;
    }
    if (!form.customerName.trim()) {
      setMessage({ type: 'error', text: 'Customer name is required.' });
      setTimeout(() => setMessage(null), 5000);
      return false;
    }
    if (!form.items.every((item) => item.itemname && Number(item.quantity) > 0 && Number(item.rate) >= 0)) {
      setMessage({ type: 'error', text: 'All items must have a name, valid quantity, and rate.' });
      setTimeout(() => setMessage(null), 5000);
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setForm({
      customerId: '',
      customerName: '',
      customerContact: { address: '', phone: '', email: '', gstin: '' },
      items: [{ code: '', itemname: '', hsnSac: '', quantity: 1, unit: 'Nos', rate: 10, taxRate: 18 }],
      isOtherState: false,
      paymentTerms: 'Cash',
      validUntil: '',
      remarks: '',
      isCancelled: false,
      manualRoundOff: '',
      invoiceNumber: '',
      date: new Date().toISOString().slice(0, 10),
      company,
    });
    setErrors({
      customerId: '',
      customerName: '',
      email: '',
      phone: '',
      gstin: '',
      items: [{ code: '', itemname: '', hsnSac: '', rate: '', taxRate: '' }],
    });
    setCurrentBillId(null);
    
    // Generate next bill number without incrementing localStorage
    const prefix = companyPrefixes[selectedCompanyFromState] || companyPrefixes[company?.name] || 'WNF';
    const storageKey = `lastBillNumber_${prefix}`;
    const lastNumber = parseInt(localStorage.getItem(storageKey) || '0');
    const nextNumber = lastNumber + 1;
    const nextInvoiceNumber = `${prefix}-${nextNumber}`;
    
    setForm((prev) => ({
      ...prev,
      invoiceNumber: nextInvoiceNumber,
    }));
  };

  const loadImageAsBase64 = async (url) => {
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to load image'));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      return null;
    }
  };

  const generatePDF = async (forEmail = false) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    let y = margin;

    doc.setFontSize(10);

    // Load logos for dual-logo system
    const logoConfig = getLogoConfiguration(selectedCompanyFromState);
    let logoBase64 = null;
    let centerLogoBase64 = null;
    
    // Load main logo (WYENFOS logo for dual-logo companies, or company logo for others)
    if (logoConfig.mainLogo && typeof logoConfig.mainLogo === 'string') {
      try {
        logoBase64 = await loadImageAsBase64(logoConfig.mainLogo);
      } catch (error) {
      }
    }
    
    // Try default logo if main logo fails
    if (!logoBase64) {
      try {
        logoBase64 = await loadImageAsBase64(defaultLogo);
      } catch (error) {
      }
    }
    
    // Load center logo for dual-logo companies
    if (logoConfig.isDualLogo && logoConfig.centerLogo) {
      try {
        centerLogoBase64 = await loadImageAsBase64(logoConfig.centerLogo);
      } catch (error) {
      }
    }
    
    
    // Load watermarks
    const watermarkBase64 = await loadImageAsBase64(watermark);
    const cancelledWatermarkBase64 = await loadImageAsBase64(cancelledWatermark);
    
    const logoWidth = 35;
    const logoHeight = 35;
    const logoY = y;
    let textX = margin + logoWidth + 8;
    let textY = y;

    // Add logo if available and valid
    if (logoBase64) {
      try {
        // Validate base64 format before adding to PDF
        if (logoBase64.startsWith('data:image/')) {
      doc.addImage(logoBase64, 'PNG', margin, logoY, logoWidth, logoHeight);
        } else {
          textX = margin;
        }
      } catch (logoError) {
        textX = margin;
      }
    } else {
      textX = margin;
    }

    // Add watermark with error handling
    if (watermarkBase64) {
      try {
        if (watermarkBase64.startsWith('data:image/')) {
      doc.setGState(new doc.GState({ opacity: 0.1 }));
      doc.addImage(watermarkBase64, 'PNG', pageWidth / 2 - 50, pageHeight / 2 - 50, 100, 100);
      doc.setGState(new doc.GState({ opacity: 1 }));
        }
      } catch (watermarkError) {
      }
    }

    // Add cancelled watermark if bill is cancelled
    if (form.isCancelled && cancelledWatermarkBase64) {
      try {
        if (cancelledWatermarkBase64.startsWith('data:image/')) {
      doc.setGState(new doc.GState({ opacity: 0.4 }));
      doc.addImage(cancelledWatermarkBase64, 'PNG', pageWidth / 2 - 50, pageHeight / 2 - 50, 100, 100);
      doc.setGState(new doc.GState({ opacity: 1 }));
        }
      } catch (cancelledWatermarkError) {
      }
    }

    // Company header - Left side with logo
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(company.name || 'WYENFOS BILLS', textX, textY);
    textY += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Address: ${company.address || 'N/A'}`, textX, textY);
    textY += 6;
    doc.text(`Mobile: ${company.mobile || 'N/A'}`, textX, textY);
    textY += 6;
    doc.text(`Email: ${company.email || 'N/A'}`, textX, textY);
    textY += 6;
    doc.text(`Website: ${company.website || 'N/A'}`, textX, textY);
    textY += 6;
    doc.text(`GSTIN: ${company.GSTIN || 'N/A'}`, textX, textY);
    textY += 6;
    doc.text(`State: ${company.state || 'N/A'} (Code: ${company.stateCode || 'N/A'})`, textX, textY);

    // Invoice details - Right side
    const rightX = pageWidth - margin - 30;
    const rightY = y;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`Bill No: ${form.invoiceNumber || 'Pending'}`, rightX, rightY);
    doc.text(`Date: ${new Date(form.date).toLocaleDateString('en-IN')}`, rightX, rightY + 6);

    y += logoHeight + 15;

    // Bill title - Moved down more
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('CASH BILL', pageWidth / 2, y, { align: 'center' });

    y += 20;

    // Customer details - Compact layout
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Customer Information:', margin, y);
    y += 8;

    const customerData = [
      ['Customer ID:', form.customerId || 'N/A', 'Phone:', form.customerContact?.phone || 'N/A'],
      ['Name:', form.customerName || 'N/A', 'Email:', form.customerContact?.email || 'N/A'],
      ['Address:', form.customerContact?.address || 'N/A', 'GSTIN:', form.customerContact?.gstin || 'N/A'],
      ['Payment Method:', form.paymentTerms || 'N/A', '', '']
    ];

    for (let i = 0; i < customerData.length; i++) {
      const yLine = y + i * 5;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(customerData[i][0], margin, yLine);
      doc.text(customerData[i][2], pageWidth / 2 + 5, yLine);
      doc.setFont('helvetica', 'normal');
      doc.text(customerData[i][1], margin + 35, yLine);
      doc.text(customerData[i][3], pageWidth / 2 + 40, yLine);
    }

    y += customerData.length * 5 + 8;

    // Add center logo for dual-logo companies
    if (logoConfig.isDualLogo && centerLogoBase64) {
      try {
        const centerLogoWidth = 40;
        const centerLogoHeight = 25;
        const centerLogoX = (pageWidth - centerLogoWidth) / 2; // Center horizontally
        
        if (centerLogoBase64.startsWith('data:image/')) {
          doc.addImage(centerLogoBase64, 'PNG', centerLogoX, y, centerLogoWidth, centerLogoHeight);
          y += centerLogoHeight + 10; // Add space after center logo
        }
      } catch (centerLogoError) {
      }
    }

    const itemHead = [['SI No', 'Item Code', 'Item Name', 'HSN/SAC', 'Qty', 'Rate', 'Total', 'Tax %']];
    const itemBody = form.items.map((item, i) => {
      const rate = typeof item.rate === 'number' ? item.rate : (parseFloat(item.rate) || 0);
      const quantity = typeof item.quantity === 'number' ? item.quantity : (parseFloat(item.quantity) || 1);
      const total = quantity * rate;
      return [
        i + 1,
        item.code || '',
        item.itemname || '',
        item.hsnSac || '',
        quantity.toString(),
        `Rs.${rate.toFixed(2)}`,
        `Rs.${total.toFixed(2)}`,
        `${item.taxRate || 18}%`,
      ];
    });

    autoTable(doc, {
      startY: y,
      head: itemHead,
      body: itemBody,
      theme: 'grid',
      headStyles: { fillColor: [155, 119, 142], halign: 'center' },
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 2, overflow: 'linebreak' },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 25 },
        2: { cellWidth: 40 },
        3: { cellWidth: 25 },
        4: { cellWidth: 15 },
        5: { cellWidth: 25 },
        6: { cellWidth: 25 },
        7: { cellWidth: 20 },
      },
    });

    const tableY = doc.lastAutoTable.finalY + 6;

    const totals = calculateTotal();
    doc.setFont('helvetica', 'bold');
    let yPos = tableY;

    const rightAlignX = pageWidth - margin - 5;
    doc.text(`Taxable Amount: Rs.${totals.taxableAmount.toFixed(2)}`, rightAlignX, yPos, { align: 'right' }); yPos += 6;

    if (form.isOtherState) {
      doc.text(`IGST: Rs.${totals.igstTotal.toFixed(2)}`, rightAlignX, yPos, { align: 'right' }); yPos += 6;
    } else {
      doc.text(`CGST: Rs.${totals.cgstTotal.toFixed(2)}`, rightAlignX, yPos, { align: 'right' }); yPos += 6;
      doc.text(`SGST: Rs.${totals.sgstTotal.toFixed(2)}`, rightAlignX, yPos, { align: 'right' }); yPos += 6;
    }

    doc.text(`Round Off: Rs.${totals.roundOff.toFixed(2)}`, rightAlignX, yPos, { align: 'right' }); yPos += 6;
    doc.text(`Grand Total: Rs.${totals.rounded.toFixed(2)}`, rightAlignX, yPos, { align: 'right' }); yPos += 10;

    doc.setFont('helvetica', 'bold');
    doc.text('Remarks:', margin, yPos);
    doc.setFont('helvetica', 'normal');
    const remarksLines = doc.splitTextToSize(form.remarks || 'N/A', pageWidth - 2 * margin);
    doc.text(remarksLines, margin, yPos + 6);
    yPos += 6 * remarksLines.length + 10;

    doc.setFont('helvetica', 'bold');
    doc.text('Terms & Conditions', margin, yPos);
    doc.setFont('helvetica', 'normal');
    const terms = [
      '1. This cash bill is issued as per agreed terms.',
      '2. Contact us within 7 days for discrepancies.',
      '3. Amount credited can be adjusted against future invoices.',
    ];
    terms.forEach((term, i) => doc.text(term, margin, yPos + 6 + i * 6));
    yPos += 30;

    // Bank Details and QR Code on same line
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Bank Details', margin, yPos);
    yPos += 8;

    // Bank details on left side
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    
    
    // Use actual bank details from state, with fallbacks
    const bankDetailsText = [
      `Company name: ${company.name || 'N/A'}`,
      `Account number: ${bankDetails.accountNumber || '10192468394'}`,
      `IFSC: ${bankDetails.ifsc || 'IDFB0080732'}`,
      `SWIFT code: ${bankDetails.swiftCode || 'IDFBINBBMUM'}`,
      `Bank name: ${bankDetails.bankName || 'IDFC FIRST'}`,
      `Branch: ${bankDetails.branch || 'THRISSUR - EAST FORT THRISSUR BRANCH'}`,
    ];
    
    // Display bank details on left side
    bankDetailsText.forEach((line, i) => {
      doc.text(line, margin, yPos + (i * 4));
    });

    // Add QR code on right side of bank details
    if (qrCodeData) {
      try {
        // Generate QR code as data URL
        const qrBase64 = await QRCode.toDataURL(qrCodeData, {
          width: 80,
          height: 80,
          color: {
            dark: '#000000',
            light: '#ffffff'
          },
          errorCorrectionLevel: 'H'
        });
        
        // Add QR code to PDF - on same line as bank details
        const qrX = pageWidth - margin - 60;
        const qrY = yPos - 5;
        doc.addImage(qrBase64, 'PNG', qrX, qrY, 40, 40);
        doc.setFontSize(8);
        doc.text('Scan to Pay', qrX + 20, qrY + 45, { align: 'center' });
      } catch (error) {
      }
    }

    yPos += bankDetailsText.length * 4 + 15;

    // Signature - positioned lower and better aligned
    const signatureX = pageWidth - margin;
    const signatureY = yPos + 10; // move slightly higher
    
    // Add some spacing line for signature
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(12);
    doc.text('____________________', signatureX, signatureY, { align: 'right' });
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Authorized Signatory', signatureX, signatureY + 6, { align: 'right' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(company.name || '', signatureX, signatureY + 12, { align: 'right' });

    doc.setFontSize(8);
    doc.text('Page 1/1', pageWidth - margin, pageHeight - 6, { align: 'right' });

    return forEmail ? doc.output('blob') : doc;
  };

  const downloadPDF = async () => {
    const doc = await generatePDF();
    if (doc) {
      try {
        const safeInvoiceNumber = (form.invoiceNumber || Date.now()).toString().replace(/[^a-zA-Z0-9]/g, '_');
        const fileName = `Cash_Bill_${safeInvoiceNumber}.pdf`;
        doc.save(fileName);
      } catch (error) {
        alert('Failed to download PDF. Please try again.');
      }
    }
  };

  const sendEmail = async () => {
    
    if (!validateForm()) {
      alert('Please fix validation errors');
      return;
    }
    if (!form.customerContact.email) {
      alert('Customer email required');
      return;
    }
    
    setLoading(true);
    
    // Add timeout for the entire email process
    const emailTimeout = setTimeout(() => {
      setLoading(false);
      setMessage({
        type: 'error',
        text: 'Email request timed out. Please try again.',
      });
      setTimeout(() => setMessage(null), 5000);
    }, 30000); // 30 second timeout
    
    try {
      // Generate PDF first (before saving) with timeout
      const pdfPromise = generatePDF(true);
      const pdfBlob = await Promise.race([
        pdfPromise,
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('PDF generation timed out')), 15000)
        )
      ]);
      
      if (!pdfBlob) {
        throw new Error('PDF generation failed');
      }
      
      const base64Pdf = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = () => reject(new Error('Failed to read PDF blob'));
        reader.readAsDataURL(pdfBlob);
      });

      const emailData = {
        billData: {
          invoiceNumber: form.invoiceNumber,
          customerName: form.customerName,
          customerId: form.customerId,
          customerContact: form.customerContact,
          date: form.date,
          paymentTerms: form.paymentTerms,
          items: form.items,
          totals: calculateTotal(),
          company: company,
          isCancelled: form.isCancelled,
          remarks: form.remarks,
          terms: form.terms
        },
        email: form.customerContact.email,
        emailTo: form.customerContact.email,
        subject: `Cash Bill #${form.invoiceNumber} - ${company.name || ''}`,
        body: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h2 style="color: #b39eb5; border-bottom: 2px solid #b39eb5; padding-bottom: 10px;">
              Cash Bill from ${company.name || ''}
            </h2>
            
          <p>Dear ${form.customerName},</p>
            
            <p>Please find attached your cash bill for the purchased items.</p>
            
            <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #333;">Bill Details:</h3>
              <ul style="margin: 10px 0; padding-left: 20px;">
                <li><strong>Bill Number:</strong> ${form.invoiceNumber}</li>
                <li><strong>Customer ID:</strong> ${form.customerId}</li>
                <li><strong>Date:</strong> ${form.date}</li>
                <li><strong>Payment Method:</strong> ${form.paymentTerms}</li>
                <li><strong>Subtotal:</strong> ₹${(calculateTotal().taxableAmount || 0).toFixed(2)}</li>
                <li><strong>CGST (9%):</strong> ₹${(calculateTotal().cgstTotal || 0).toFixed(2)}</li>
                <li><strong>SGST (9%):</strong> ₹${(calculateTotal().sgstTotal || 0).toFixed(2)}</li>
                <li><strong>Grand Total:</strong> ₹${(calculateTotal().rounded || 0).toFixed(2)}</li>
                <li><strong>Status:</strong> ${form.isCancelled ? 'Cancelled' : 'Active'}</li>
          </ul>
            </div>
            
            <p><strong>Payment Information:</strong></p>
            <ul style="margin: 10px 0; padding-left: 20px;">
              <li>Payment has been received as per the bill</li>
              <li>Please keep this bill for your records</li>
              <li>For any discrepancies, please contact us within 7 days</li>
            </ul>
            
          <p>Thank you for your business!</p>
            
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd;">
              <p style="margin: 0;"><strong>Best regards,</strong><br>
              ${company.name || ''}<br>
              Thekkekara Arcade, Chelakottukara, Thrissur, Kerala, 680001<br>
              Mobile: 8547014116 | Email: wyenfos@gmail.com<br>
              Website: www.wyenfos.com</p>
            </div>
          </div>
        `,
        pdfBase64: base64Pdf,
      };
      
      
      // Add timeout for the API request
      try {
      const response = await Promise.race([
        api.post('/cashbills/send-email', emailData),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Email request timed out')), 15000)
        )
      ]);
      
      clearTimeout(emailTimeout);
      setMessage({ type: 'success', text: 'Email sent successfully!' });
      } catch (apiError) {
      clearTimeout(emailTimeout);
        
        // Generate PDF and provide download with email instructions
        try {
          const doc = await generatePDF(true); // Generate for email
          const pdfBlob = doc.output('blob');
          const pdfUrl = URL.createObjectURL(pdfBlob);
          
          // Create download link
          const downloadLink = document.createElement('a');
          downloadLink.href = pdfUrl;
          downloadLink.download = `${form.invoiceNumber || 'cash-bill'}.pdf`;
          downloadLink.click();
          
          // Show instructions for manual email
          setMessage({
            type: 'warning',
            text: `Email service unavailable. PDF downloaded - please send manually to: ${form.customerContact.email}`,
          });
          
          // Copy email content to clipboard
          const emailContent = `Subject: Cash Bill ${form.invoiceNumber}

Dear ${form.customerName},

Please find attached your cash bill for the purchased items.

Bill Details:
- Bill Number: ${form.invoiceNumber}
- Customer ID: ${form.customerId}
- Date: ${form.date}
- Payment Method: ${form.paymentTerms}
- Grand Total: ₹${(calculateTotal().rounded || 0).toFixed(2)}

Thank you for your business!

Best regards,
${company.name || 'WYENFOS BILLS'}`;
          
          navigator.clipboard.writeText(emailContent).catch(() => {
          });
          
        } catch (pdfError) {
      setMessage({
        type: 'error',
            text: `Email service unavailable and PDF generation failed. Please contact customer manually at: ${form.customerContact.email}`,
      });
        }
      }
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const printBill = async () => {
    try {
      const doc = await generatePDF(); // already creates your structured PDF
      if (!doc) throw new Error("PDF generation failed");
  
      doc.autoPrint(); // tells browser to open print dialog
      const pdfBlobUrl = doc.output('bloburl'); 
      window.open(pdfBlobUrl); // opens PDF in a new tab, print dialog shows
    } catch (err) {
      alert("Failed to generate print view. Please try again.");
    }
  };  

  const requestEditPermission = async () => {
    if (!pendingEditBill) return false;
    
    try {
      setLoading(true);
      
      // Try to send notification through API first
      if (api && !API_CONFIG.USE_FIREBASE_ONLY) {
        try {
          const response = await api.post('/notifications/request-permission', {
            type: 'edit_request',
            billType: 'cashbill',
            billId: pendingEditBill.id || pendingEditBill._id,
            billData: pendingEditBill,
            requestedBy: userEmail,
            requestedByRole: userRole,
            reason: 'Staff requested permission to edit cash bill',
            status: 'pending'
          });

          if (response.data.success) {
            setMessage({ 
              type: 'success', 
              text: `Edit request sent to Super Admin for bill ${pendingEditBill.invoiceNumber}. You will be notified once approved.` 
            });
            return true;
          }
        } catch (apiError) {
        }
      }
      
      // Fallback: Save request to Firebase directly
      const requestData = {
        type: 'edit_request',
        billType: 'cashbill',
        billId: pendingEditBill.id || pendingEditBill._id,
        billData: pendingEditBill,
        userEmail: userEmail,
        userRole: userRole,
        reason: 'Staff requested permission to edit cash bill',
        status: 'pending',
        requestedBy: userEmail,
        invoiceNumber: pendingEditBill.invoiceNumber,
        customerName: pendingEditBill.customerName,
        requestedAt: new Date().toISOString()
      };
      
      // Save to Firebase notifications collection
      await FirebaseService.addNotification(requestData);
      
      setMessage({ 
        type: 'success', 
        text: `Edit request sent to Super Admin for bill ${pendingEditBill.invoiceNumber}. Check your email for updates.` 
      });
      return true;
      
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: `Failed to send edit request: ${error.message}` 
      });
      return false;
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const requestDeletePermission = async () => {
    if (!pendingEditBill) return false;
    
    try {
      setLoading(true);
      
      // Try to send notification through API first
      if (api && !API_CONFIG.USE_FIREBASE_ONLY) {
        try {
          const response = await api.post('/notifications/request-permission', {
            type: 'delete_request',
            billType: 'cashbill',
            billId: pendingEditBill.id || pendingEditBill._id,
            billData: pendingEditBill,
            requestedBy: userEmail,
            requestedByRole: userRole,
            reason: 'Staff requested permission to delete cash bill',
            status: 'pending'
          });

          if (response.data.success) {
            setMessage({ 
              type: 'success', 
              text: `Delete request sent to Super Admin for bill ${pendingEditBill.invoiceNumber}. You will be notified once approved.` 
            });
            return true;
          }
        } catch (apiError) {
        }
      }
      
      // Fallback: Save request to Firebase directly
      const requestData = {
        type: 'delete_request',
        billType: 'cashbill',
        billId: pendingEditBill.id || pendingEditBill._id,
        billData: pendingEditBill,
        userEmail: userEmail,
        userRole: userRole,
        reason: 'Staff requested permission to delete cash bill',
        status: 'pending',
        requestedBy: userEmail,
        invoiceNumber: pendingEditBill.invoiceNumber,
        customerName: pendingEditBill.customerName,
        requestedAt: new Date().toISOString()
      };
      
      // Save to Firebase notifications collection
      await FirebaseService.addNotification(requestData);
      
      setMessage({ 
        type: 'success', 
        text: `Delete request sent to Super Admin for bill ${pendingEditBill.invoiceNumber}. Check your email for updates.` 
      });
      return true;
      
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: `Failed to send delete request: ${error.message}` 
      });
      return false;
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const requestEditBill = (billId) => {
    const bill = savedBills.find((b) => b.id === billId || b._id === billId);
    if (bill) {
      if (editPermissions[bill.id || bill._id]) {
        // User has permission, proceed with edit
        setForm({
          invoiceNumber: bill.invoiceNumber,
          customerId: bill.customerId,
          customerName: bill.customerName,
          customerContact: {
            address: bill.customerAddress || '',
            phone: bill.customerContact?.phone || '',
            email: bill.customerContact?.email || '',
            gstin: bill.customerContact?.gstin || '',
          },
          items: bill.items || [{ code: '', itemname: '', hsnSac: '', quantity: 1, unit: 'Nos', rate: '', taxRate: '' }],
          isOtherState: bill.isOtherState || false,
          paymentTerms: bill.paymentTerms || 'Cash',
          remarks: bill.remarks || '',
          isCancelled: bill.isCancelled || false,
          date: bill.date ? new Date(bill.date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
          company: bill.company || company,
        });
        setCurrentBillId(bill._id);
        setMessage({ type: 'success', text: 'Bill loaded for editing' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        // User doesn't have permission, request it
        setPendingEditBill(bill);
        setShowPermissionModal(true);
      }
    }
  };

  const handlePermissionResponse = (approved) => {
    if (approved && pendingEditBill) {
      setEditPermissions((prev) => ({ ...prev, [pendingEditBill._id]: true }));
      requestEditBill(pendingEditBill._id);
    } else {
      setMessage({ type: 'error', text: 'Permission denied' });
    }
    setShowPermissionModal(false);
    setPendingEditBill(null);
  };

  if (loading || !Array.isArray(form.items)) {
    return <div>Loading...</div>;
  }

  const totals = calculateTotal();

  return (
    <ErrorBoundary>
      <div className="cashbill-page-wrapper">
        <div className="cashwatermark">
          <img src={watermark} alt="Watermark" />
        </div>
        {form.isCancelled && (
          <div className="cashcancelled-watermark">
            <img src={cancelledWatermark} alt="Cancelled" />
          </div>
        )}
        <div className="cashbillinvoice-container print-content">
          {/* Permission status is now handled via email notifications */}
          <div className="bill-header">
            {/* Logo and Company Address Section - Positioned on the left side as requested */}
            <div className="header-left-section">
              <div className="company-logo-container-header">
                {(() => {
                  const logoConfig = getLogoConfiguration(selectedCompanyFromState);
                  return (
                    <img
                      src={logoConfig.mainLogo}
                      alt="Company Logo"
                      className="company-logo-header"
                      onError={(e) => {
                        e.target.src = defaultLogo;
                      }}
                    />
                  );
                })()}
              </div>
              <div className="company-address-header">
                <h4 className="company-name-header">{selectedCompanyFromState}</h4>
                <p className="address-text-header">{companyDetails.address}</p>
                <div className="company-details-row">
                  {companyDetails.GSTIN && <p className="gstin-text-header">GSTIN: {companyDetails.GSTIN}</p>}
                  {companyDetails.email && <p className="contact-text-header">{companyDetails.email}</p>}
                </div>
                <div className="company-details-row">
                  {companyDetails.mobile && <p className="contact-text-header mobile-number-left">{companyDetails.mobile}</p>}
                  {companyDetails.website && <p className="contact-text-header">{companyDetails.website}</p>}
                </div>
              </div>
            </div>
            
            <div className="bill-info-header">
              <h2>CASH BILL{form.isCancelled ? ' (CANCELLED)' : ''}</h2>
              <p className="bill-number">
                CASH BILL No: {form.invoiceNumber || 'Pending'}
                {userRole === 'super_admin' && (
                  <button
                    type="button"
                    className="btn-reset-counter no-print"
                    onClick={() => {
                      const companyName = selectedCompanyFromState;
                      const result = window.resetBillCounter(companyName);
                      if (result) {
                        setMessage({ 
                          type: 'success', 
                          text: `Bill counter reset! Next bill will be ${result}` 
                        });
                        setTimeout(() => setMessage(null), 3000);
                      }
                    }}
                    title="Reset bill counter to start from 1 (Super Admin only)"
                  >
                    🔄 Reset Counter
                  </button>
                )}
              </p>
              <p className="date">Date: {form.date || new Date().toLocaleDateString('en-IN')}</p>
            </div>
          </div>
          
          {/* Center logo for dual-logo companies */}
          {(() => {
            const logoConfig = getLogoConfiguration(selectedCompanyFromState);
            if (logoConfig.isDualLogo && logoConfig.centerLogo) {
              return (
                <div className="center-logo-container">
                  <img
                    src={logoConfig.centerLogo}
                    alt={logoConfig.centerLogoAlt}
                    className="center-company-logo"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
            />
          </div>
              );
            }
            return null;
          })()}
          
          <hr className="invoice-divider" />
          {message && <div className={`message ${message.type}`}>{message.text}</div>}

          {showPermissionModal && pendingEditBill && (
            <div className="modal">
              <div className="modal-content">
                <h3>Edit Permission Request</h3>
                <p>Approve editing bill {pendingEditBill.invoiceNumber}?</p>
                <div className="modal-buttons">
                  <button type="button" onClick={() => handlePermissionResponse(true)}>
                    Approve
                  </button>
                  <button type="button" onClick={() => handlePermissionResponse(false)}>
                    Deny
                  </button>
                </div>
              </div>
            </div>
          )}

          {showManualEntryModal && (
            <div className="modal">
              <div className="modal-content manual-entry-modal">
                <h3>📝 Manual Product Entry</h3>
                <p className="modal-subtitle">Enter product details manually when product rates have errors or product is not found in system.</p>
                
                <div className="manual-entry-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Product Name *</label>
                      <input
                        type="text"
                        value={manualEntryData.itemname}
                        onChange={(e) => setManualEntryData(prev => ({ ...prev, itemname: e.target.value }))}
                        placeholder="Enter product name"
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>Item Code</label>
                      <input
                        type="text"
                        value={manualEntryData.code}
                        onChange={(e) => setManualEntryData(prev => ({ ...prev, code: e.target.value }))}
                        placeholder="Enter item code"
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>HSN/SAC Code</label>
                      <input
                        type="text"
                        value={manualEntryData.hsnSac}
                        onChange={(e) => setManualEntryData(prev => ({ ...prev, hsnSac: e.target.value }))}
                        placeholder="Enter HSN/SAC code"
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>Quantity *</label>
                      <input
                        type="number"
                        value={manualEntryData.quantity}
                        onChange={(e) => setManualEntryData(prev => ({ ...prev, quantity: Math.max(1, parseInt(e.target.value) || 1) }))}
                        min="1"
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Rate (₹) *</label>
                      <input
                        type="number"
                        value={manualEntryData.rate}
                        onChange={(e) => setManualEntryData(prev => ({ ...prev, rate: Math.max(0, parseFloat(e.target.value) || 0) }))}
                        step="0.01"
                        min="0"
                        placeholder="Enter rate"
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>GST Rate (%) *</label>
                      <input
                        type="number"
                        value={manualEntryData.taxRate}
                        onChange={(e) => setManualEntryData(prev => ({ ...prev, taxRate: Math.max(0, parseFloat(e.target.value) || 18) }))}
                        step="0.01"
                        min="0"
                        placeholder="Enter GST rate"
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div className="manual-entry-preview">
                    <h4>Preview:</h4>
                    <div className="preview-item">
                      <strong>{manualEntryData.itemname || 'Product Name'}</strong>
                      <div className="preview-details">
                        <span>Code: {manualEntryData.code || 'N/A'}</span>
                        <span>HSN: {manualEntryData.hsnSac || 'N/A'}</span>
                        <span>Qty: {manualEntryData.quantity}</span>
                        <span>Rate: ₹{manualEntryData.rate || '0.00'}</span>
                        <span>GST: {manualEntryData.taxRate || '18'}%</span>
                        <span>Total: ₹{((manualEntryData.quantity || 1) * (manualEntryData.rate || 0)).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-buttons">
                  <button type="button" onClick={saveManualEntry} className="btn-primary">
                    ✅ Save Product
                  </button>
                  <button type="button" onClick={closeManualEntryModal} className="btn-secondary">
                    ❌ Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="customer-search no-print">
            <div className="search-input-group">
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Search customers by ID, name, phone, or email"
                className="form-control cashbill-input"
                disabled={form.isCancelled}
              />
              <button
                className="search-button no-print"
                onClick={() => debouncedSearch(customerSearch)}
                disabled={customerSearch.length < 3 || loading}
              >
                🔍
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((customer) => (
                  <div
                    key={customer._id || customer.customerId}
                    onClick={() => selectCustomer(customer)}
                    className="search-result-item"
                  >
                    {customer.customerId} - {customer.customerName} ({Array.isArray(customer.company) ? customer.company.join(', ') : customer.company || 'N/A'})
                    {customer.customerContact.phone && ` - ${customer.customerContact.phone}`}
                    {customer.customerContact.email && ` - ${customer.customerContact.email}`}
                  </div>
                ))}
              </div>
            )}
            {customerSearch.trim() && searchResults.length === 0 && (
              <div className="customer-info-section">
                <div className="customer-not-found-info">
                  <p className="info-message">
                    ℹ️ No customer found with name: <strong>{customerSearch}</strong>
                  </p>
                  <p className="info-suggestion">
                    You can continue by filling in the customer details manually below.
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="customer-details">
            <div className="cashbill-input">
              {[
                {
                  label: 'Customer ID',
                  field: 'customerId',
                  type: 'text',
                  placeholder: 'Customer ID (e.g., CUST-1)',
                  required: true,
                                      pattern: '^CUST-\\d+$',
                                      errorMessage: 'Must be in format CUST- followed by numbers',
                },
                {
                  label: 'Name',
                  field: 'customerName',
                  type: 'text',
                  placeholder: 'Customer Name',
                  required: true,
                  minLength: 2,
                },
                {
                  label: 'Date',
                  field: 'date',
                  type: 'date',
                  required: true,
                },
                {
                  label: 'Address',
                  field: 'address',
                  type: 'text',
                  placeholder: 'Address',
                },
                {
                  label: 'Phone',
                  field: 'phone',
                  type: 'tel',
                  placeholder: 'Phone Number',
                },
                {
                  label: 'Email',
                  field: 'email',
                  type: 'email',
                  placeholder: 'Email',
                  errorMessage: 'Invalid email format',
                },
                {
                  label: 'GSTIN',
                  field: 'gstin',
                  type: 'text',
                  placeholder: '15-digit GSTIN (optional)',
                  pattern: '^([0-9A-Z]{15})?$',
                  maxLength: 15,
                  errorMessage: 'Must be 15 alphanumeric characters',
                  transform: (value) => value.toUpperCase().replace(/\s/g, ''),
                },
              ].map(({ label, field, type, placeholder, required, pattern, maxLength, errorMessage, transform }) => (
                <div key={field} className="input-group">
                  <label>
                    {label}:
                    {required && <span className="required-asterisk">*</span>}
                  </label>
                  <input
                    type={type}
                    value={field === 'customerId' || field === 'customerName' || field === 'date'
                      ? form[field]
                      : form.customerContact[field] || ''}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (transform) value = transform(value);
                      if (field === 'customerId') {
                        updateCustomerId(value);
                      } else if (field === 'customerName') {
                        // Don't call updateCustomerName if we're in the middle of creating a customer
                        if (!isCreatingCustomer) {
                        updateCustomerName(value);
                        } else {
                          // Just update the form directly without searching for existing customers
                          setForm((prev) => ({ ...prev, customerName: value }));
                        }
                      } else if (field === 'date') {
                        setForm((prev) => ({ ...prev, [field]: value }));
                      } else {
                        updateCustomerContact(field, value);
                      }
                    }}
                    placeholder={placeholder}
                    className={`form-control cashbill-input ${errors[field] ? 'input-error' : ''}`}
                    disabled={form.isCancelled}
                    required={required}
                    pattern={pattern}
                    maxLength={maxLength}
                  />
                  {errors[field] && (
                    <span className="error">
                      {errorMessage || errors[field]}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="customer-form-actions">
              {form.customerId && (
                <div className="update-customer-section">
                  <button
                    type="button"
                    className="btn-update-customer no-print"
                    onClick={() => updateAllCustomerDetails(form.customerId)}
                    disabled={loading}
                    title="Save current customer details to database"
                  >
                    💾 Update Customer Details
                  </button>
                  <small className="update-help-text">
                    Click to save the current contact details to the customer database
                  </small>
                </div>
              )}
              
              <div className="cashsave-customer-section no-print">
                <button
                  type="button"
                  onClick={saveCustomerDetails}
                  className="save-customer-btn"
                  disabled={loading || !form.customerName.trim()}
                  title="Save customer details to database"
                >
                  💾 Save Customer Details
                </button>
                <div className="save-customer-info">
                  <small>💡 Save customer information for future use. Customer ID will be auto-generated if not provided.</small>
                </div>
              </div>
              
            </div>
          </div>
          <div className="input-group">
            <label>Payment Method:</label>
            <select
              value={form.paymentTerms}
              onChange={(e) => setForm((prev) => ({ ...prev, paymentTerms: e.target.value }))}
              className="form-control cashbill-input"
              disabled={form.isCancelled}
            >
              <option value="Cash">Cash</option>
              <option value="Bank">Bank</option>
              <option value="UPI">UPI</option>
              <option value="CARD">CARD</option>
              <option value="Advance">Advance Payment</option>
            </select>
          </div>
          <div className="tax-options">
            <label>
              <input
                type="checkbox"
                checked={form.isOtherState}
                onChange={(e) => setForm((prev) => ({ ...prev, isOtherState: e.target.checked }))}
                disabled={form.isCancelled}
              />
              Other State (IGST)
            </label>
            <label>
              <input
                type="checkbox"
                checked={form.isCancelled}
                onChange={(e) => setForm((prev) => ({ ...prev, isCancelled: e.target.checked }))}
              />
              Cancel
            </label>
          </div>
          <div className="cash-bill-items">
            <div className="items-header">
            <h3>ITEMS</h3>
              <div className="items-instructions">
                <span className="instruction-text">
                  💡 <strong>Product Edit Instructions:</strong> Use the search icon (🔍) to find products, or click the edit icon (✏️) to manually enter product details. 
                  Click the settings icon (⚙️) to open field editors for price, GST, HSN, and quantity. All fields in the table below are also directly editable to correct any purchase admin errors.
                </span>
              </div>
            </div>
            <div className="product-table-wrapper">
              <table className="cashbillitems-section">
                <thead>
                  <tr>
                    <th className="col-sno">SI No</th>
                    <th className="col-code">Item Code</th>
                    <th className="col-name">Item Name</th>
                    <th className="col-hsn">HSN/SAC</th>
                    <th className="col-qty">Quantity</th>
                    <th className="col-rate">Rate</th>
                    <th className="col-total">Total</th>
                    <th className="col-tax">Tax Rate (%)</th>
                    <th className="col-cashbill-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, index) => (
                  <tr key={`cashbill-item-${index}`} className="cashbill-horizontal-item-row">
                    <td className="cashbill-item-sno">{index + 1}</td>
                    <td className="cashbill-item-code">
                      <input
                        type="text"
                        value={item.code}
                        onChange={(e) => updateItem(index, 'code', e.target.value)}
                        placeholder="Item Code"
                        className="cashbill-input-field"
                      />
                    </td>
                    <td className="cashbill-item-name">
                      <div className="cashbill-product-search-container">
                        <div className="cashbill-input-with-edit">
                          <ProductSelector
                            value={item.itemname}
                            onChange={(value) => updateItem(index, 'itemname', value)}
                            onProductSelect={(product) => handleProductSelect(index, product)}
                            placeholder="Search products..."
                            disabled={form.isCancelled}
                            showEditMode={false}
                            showFieldEditors={false}
                          />
                          <button
                            type="button"
                            className="cashbill-action-btn cashbill-edit-btn"
                            onClick={() => openManualEntryModal(index)}
                            disabled={form.isCancelled}
                            title="Edit product details"
                          >
                            ✏️
                          </button>
                        </div>
                        <div className="cashbill-product-actions">
                        </div>
                      </div>
                    </td>
                    <td className="cashbill-item-hsn">
                      <input
                        type="text"
                        value={item.hsnSac}
                        onChange={(e) => updateItem(index, 'hsnSac', e.target.value)}
                        placeholder="HSN/SAC"
                        className="cashbill-input-field"
                      />
                    </td>
                    <td className="cashbill-item-qty">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        placeholder="Qty"
                        className="cashbill-input-field"
                      />
                    </td>
                    <td className="cashbill-item-rate">
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => updateItem(index, 'rate', e.target.value)}
                        placeholder="Rate"
                        className="cashbill-input-field"
                      />
                    </td>
                    <td className="cashbill-item-total">₹{(item.quantity * item.rate).toFixed(2)}</td>
                    <td className="cashbill-item-tax">
                      <input
                        type="number"
                        value={item.taxRate}
                        onChange={(e) => updateItem(index, 'taxRate', e.target.value)}
                        placeholder="Tax %"
                        className="cashbill-input-field"
                      />
                    </td>
                    <td className="cashbill-item-action">
                      <button 
                        onClick={() => deleteRow(index)}
                        className="cashbill-delete-btn"
                        title="Delete item"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="add-itemm-btn no-print" onClick={addNewRow} disabled={form.isCancelled}>
              + Add Item
            </button>
          </div>
          <div className="main-bill-section">
            <div className="bill-content">
              <div className="totals-remarks-wrapper">
                <div className="totals-section">
                  <div className="amount-details">
                    <div className="amount-row">
                      <span>Taxable Amount:</span> <span>₹{totals.taxableAmount.toFixed(2)}</span>
                    </div>
                    {form.isOtherState ? (
                      <div className="amount-row">
                        <span>IGST:</span> <span>₹{totals.igstTotal.toFixed(2)}</span>
                      </div>
                    ) : (
                      <>
                        <div className="amount-row">
                          <span>CGST:</span> <span>₹{totals.cgstTotal.toFixed(2)}</span>
                        </div>
                        <div className="amount-row">
                          <span>SGST:</span> <span>₹{totals.sgstTotal.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    <div className="amount-row round-off-row" data-round-off-value={totals.roundOff.toFixed(2)}>
                      <span>Round Off:</span> 
                      <div className="round-off-input-container">
                        <span className="currency-symbol">₹</span>
                        <input
                          type="number"
                          step="0.01"
                          value={form.manualRoundOff}
                          onChange={(e) => setForm((prev) => ({ ...prev, manualRoundOff: e.target.value }))}
                          placeholder={totals.roundOff.toFixed(2)}
                          className="round-off-input"
                          disabled={form.isCancelled}
                        />
                        <button
                          type="button"
                          className="auto-round-btn"
                          onClick={() => setForm((prev) => ({ ...prev, manualRoundOff: '' }))}
                          title="Reset to automatic round off"
                          disabled={form.isCancelled}
                        >
                          Auto
                        </button>
                      </div>
                    </div>
                    <div className="total-amount-row">
                      <span><strong>Grand Total:</strong></span> <span><strong>₹{totals.rounded.toFixed(2)}</strong></span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="input-group">
                <label>Remarks:</label>
                <textarea
                  value={form.remarks}
                  onChange={(e) => setForm((prev) => ({ ...prev, remarks: e.target.value }))}
                  className="form-control cashbill-input"
                  disabled={form.isCancelled}
                />
              </div>
            </div>
          </div>
          <div className="action-buttons no-print">
            <button
              className="btn-successcashsave-btn no-print"
              onClick={saveBill}
              disabled={loading || currentBillId}
            >
              Save
            </button>
         
            <button
              className="btn-primarycashdownload-btn no-print"
              onClick={downloadPDF}
              disabled={loading}
            >
              <i className="fa-solid fa-file-pdf"></i>
            </button>
            <button
              className="btn-primarycashprint-btn no-print"
              onClick={printBill}
              disabled={loading}
            >
              🖨️ Print
            </button>
            <button
              className="btn-primarycashbill-email-btn no-print"
              onClick={sendEmail}
              disabled={loading}
            >
              📧 Email
            </button>
            <button
              className="btn-secondarycashback-btn no-print"
              onClick={() => {
                navigate(-1);
              }}
              disabled={loading}
            >
              <i className="fa-solid fa-backward"></i>
            </button>
            <button
              className="btn-secondarycashreset-btn no-print"
              onClick={resetForm}
              disabled={loading}
            >
              Reset
            </button>
          </div>
             <div className="futerr">
              <div className="futer-content">
                <div className="left-section">
                  <div className="terms-section">
                    <h4>Terms & Conditions</h4>
                    <div className="terms-content">
                      <p>
                        1. This cash bill is issued as per agreed terms.<br />
                        2. Contact us within 7 days for discrepancies.<br />
                        3. Amount credited can be adjusted against future invoices.
                      </p>
                    </div>
                  </div>
                  
                  <div className="cashbill-bank-details">
                    <h4>Bank Details</h4>
                    <div className="bank-container">
                      <div className="bank-contentcashbill">
                        <p><strong>Company name:</strong> {company.name || 'WYENFOS INFOTECH PRIVATE LIMITED'}</p>
                        <p><strong>Account number:</strong> {bankDetails.accountNumber || '10192468394'}</p>
                        <p><strong>IFSC:</strong> {bankDetails.ifsc || 'IDFB0080732'}</p>
                        <p><strong>SWIFT code:</strong> {bankDetails.swiftCode || 'IDFBINBBMUM'}</p>
                        <p><strong>Bank name:</strong> {bankDetails.bankName || 'IDFC FIRST'}</p>
                        <p><strong>Branch:</strong> {bankDetails.branch || 'THRISSUR - EAST FORT THRISSUR BRANCH'}</p>
                        {bankDetails.upiId && <p><strong>UPI ID:</strong> {bankDetails.upiId}</p>}
                      </div>
                      <div className="qr-code-section">
                        {qrCodeData ? (
                          <QRCodeCanvas 
                            value={qrCodeData} 
                            size={120} 
                            level="H" 
                            includeMargin 
                            className="qr-code"
                          />
                        ) : (
                          <div className="qr-code-placeholder">
                            <div className="qr-placeholder-box">
                              <span>QR</span>
                            </div>
                          </div>
                        )}
                        <p className="qr-code-label">Scan to Pay</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
  
              <div className="signaturee">
                <strong>Authorized Signatory</strong><br />
                <strong>{company.name || ''}</strong>
              </div>
            </div>
          <div className="cashbill-saved-bills-section no-print">
            <h3>Saved Bills</h3>
            <div className="cashbill-table-responsive">
              <table className="table cashbill-saved-bills-table table-striped">
                <thead>
                  <tr>
                    <th>Bill No</th>
                    <th>Customer</th>
                    <th>Customer ID</th>
                    <th>Date</th>
                    <th>Total</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(savedBills) && savedBills.map((bill) => (
                    <tr key={bill.id || bill._id || bill.invoiceNumber}>
                      <td>{bill.invoiceNumber}</td>
                      <td>{bill.customerName}</td>
                      <td>{bill.customerId || 'N/A'}</td>
                      <td>{new Date(bill.date).toLocaleDateString('en-IN')}</td>
                      <td>₹{bill.totals?.grandTotal?.toFixed(2) || '0.00'}</td>
                      <td>
                  {editPermissions[bill.id || bill._id] ? (
                    <button className="select-btn no-print" onClick={() => requestEditBill(bill.id || bill._id)} title="Edit Bill">
                      Request Edit
                    </button>
                  ) : (
                    <button className="select-btn no-print" onClick={() => { setPendingEditBill(bill); requestEditPermission(); }} title="Request Edit Permission">
                    Request Edit

                    </button>
                  )}
                  <button
                    className="btn-danger no-print"
                    onClick={() => { setPendingEditBill(bill); requestDeletePermission(); }}
                    title="Request Delete Permission"
                  >
                    Request Delete
                  </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Mobile Card Layout for Small Screens */}
            <div className="cashbill-saved-bills-mobile-cards">
              {Array.isArray(savedBills) && savedBills.map((bill) => (
                <div key={`mobile-${bill.id || bill._id || bill.invoiceNumber}`} className="cashbill-saved-bill-card">
                  <div className="cashbill-saved-bill-card-header">
                    <div className="cashbill-saved-bill-card-title">{bill.invoiceNumber}</div>
                    <div className="cashbill-saved-bill-card-total">₹{bill.totals?.grandTotal?.toFixed(2) || '0.00'}</div>
                  </div>
                  <div className="cashbill-saved-bill-card-details">
                    <div className="cashbill-saved-bill-card-detail">
                      <strong>Customer:</strong> {bill.customerName}
                    </div>
                    <div className="cashbill-saved-bill-card-detail">
                      <strong>ID:</strong> {bill.customerId || 'N/A'}
                    </div>
                    <div className="cashbill-saved-bill-card-detail">
                      <strong>Date:</strong> {new Date(bill.date).toLocaleDateString('en-IN')}
                    </div>
                    <div className="cashbill-saved-bill-card-detail">
                      <strong>Total:</strong> ₹{bill.totals?.grandTotal?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                  <div className="cashbill-saved-bill-card-actions">
                    {editPermissions[bill.id || bill._id] ? (
                      <button className="select-btn" onClick={() => requestEditBill(bill.id || bill._id)} title="Edit Bill">
                        ✏️
                      </button>
                    ) : (
                      <button className="select-btn" onClick={() => { setPendingEditBill(bill); requestEditPermission(); }} title="Request Edit Permission">
                        📝
                      </button>
                    )}
                    <button
                      className="btn-danger"
                      onClick={() => { setPendingEditBill(bill); requestDeletePermission(); }}
                      title="Request Delete Permission"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default CashBill;
